{
  "pages": [
    {
      "guideType": "docs",
      "slug": "index",
      "title": "Welcome to Gab AI",
      "description": "Meet Gab AI: chat with leading models, create media, and use Work to research, make real files, write code, build games, and complete projects.",
      "badge": "Getting Started",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "getting-started",
      "navOrder": 0,
      "navIcon": "book-open",
      "searchKeywords": [
        "intro",
        "welcome",
        "start",
        "begin",
        "overview"
      ],
      "meta": {
        "title": "Gab AI Documentation: All-in-One AI Platform Guide",
        "description": "Meet Gab AI: chat with leading models, create media, and use Work to research, make real files, write code, build games, and complete projects.",
        "keywords": "Gab AI documentation, AI platform docs, all-in-one AI, AI tools docs, AI API documentation, voice AI, multimodal AI, image generation docs, video generation docs, AI for beginners, AI tutorial, Gab AI Work, AI projects, playable games",
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "How Work turns an idea into a finished project",
              "What Gab AI is and how it unifies multiple AI services",
              "Key features including Voice Mode, image/video/music generation",
              "Organizing your work with Bookmarks, Collections, Memory, and Custom Bots",
              "Available AI models (GPT-6, Claude, Gemini, Arya, and more)",
              "Privacy-first approach and company values"
            ],
            "readTime": 5,
            "prerequisites": []
          },
          {
            "type": "section",
            "title": "Meet Gab AI Work",
            "copy": "Work is a central part of Gab AI: an AI bot with its own computer that can carry out a project for you. Ask it to research a topic, create editable documents and spreadsheets, build a website or a playable game, or make an AI video with music and voiceover.\n\nStart a new conversation, select Work, describe what you want, and attach any useful files. Follow the progress, answer questions, and ask for changes in the same conversation. Work is included with Plus and Plus Premium."
          },
          {
            "type": "code",
            "title": "Try a complete project",
            "language": "text",
            "code": "Research beginner-friendly vegetables for a sunny patio. Create a concise PDF guide, an editable shopping-list spreadsheet, and a simple planting schedule. Include source links and explain any assumptions."
          },
          {
            "type": "quick-links",
            "items": [
              {
                "title": "Learn how to use Work",
                "label": "Learn how to use Work",
                "href": "/docs/work"
              },
              {
                "title": "Write a good Work task",
                "label": "Write a good Work task",
                "href": "/docs/work-prompts"
              }
            ]
          },
          {
            "type": "section",
            "title": "Gab AI on Mac, iOS, and Android",
            "copy": "The Mac app connects Work to folders on your computer, with file permissions, change review, and recovery history. Pair the iOS or Android app with your Mac using Remote to continue project chats from your phone. Sign in to the same account on both devices and keep the Mac awake, online, and Gab AI open while Work needs local files."
          },
          {
            "type": "quick-links",
            "title": "Mac app and Remote guides",
            "items": [
              {
                "label": "Get started with the Mac app",
                "href": "/docs/mac-app"
              },
              {
                "label": "Project folders and permissions",
                "href": "/docs/project-folders"
              },
              {
                "label": "Set up Remote on iOS or Android",
                "href": "/docs/remote"
              }
            ]
          },
          {
            "type": "section",
            "title": "What is Gab AI?",
            "copy": "Gab AI is an all-in-one AI platform that combines the world's best AI models into a single, unified experience. Instead of juggling multiple subscriptions for ChatGPT, Midjourney, and other tools, you get everything in one place with one subscription.\n\nOur intelligent routing system automatically selects the best AI model for your task, whether you're writing code, generating images, creating music, or having a voice conversation. Plus, with personalized AI memory, your assistant learns about you over time to provide increasingly relevant responses."
          },
          {
            "type": "section",
            "title": "Voice Mode — Talk to AI",
            "copy": "Have Real Conversations with AI\n\nVoice Mode transforms AI interaction into natural, spoken dialogue. Simply speak your questions and hear AI respond in real-time—just like talking to a person. Interrupt anytime, ask follow-ups, and have flowing conversations hands-free."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "items": [
              {
                "title": "Real-time responses",
                "icon": "bolt"
              },
              {
                "title": "Interrupt anytime",
                "icon": "hand"
              },
              {
                "title": "Multiple voices",
                "icon": "waveform-lines"
              },
              {
                "title": "Auto-save transcripts",
                "icon": "floppy-disk"
              }
            ]
          },
          {
            "type": "quick-links",
            "items": [
              {
                "title": "Learn More About Voice Mode",
                "label": "Learn More About Voice Mode",
                "description": "",
                "href": "/docs/voice"
              }
            ]
          },
          {
            "type": "quick-links",
            "title": "Everything You Need",
            "items": [
              {
                "title": "Work",
                "label": "Work",
                "description": "Give an AI bot a project. Research, create files, build software and games, and produce videos in one conversation.",
                "href": "/docs/work",
                "icon": "briefcase"
              },
              {
                "icon": "microphone",
                "title": "Voice Mode",
                "label": "Voice Mode",
                "description": "Have natural, real-time voice conversations with AI. Speak freely, interrupt anytime, and get instant spoken responses.",
                "href": "/docs/voice",
                "color": "#8B5CF6",
                "endpoint": ""
              },
              {
                "icon": "message",
                "title": "Multi-Model Chat",
                "label": "Multi-Model Chat",
                "description": "Access GPT-6, Claude, Gemini, DeepSeek, Kimi, Qwen, and our exclusive Arya AI—all from one interface.",
                "href": "/docs/models",
                "color": "#3B82F6",
                "endpoint": ""
              },
              {
                "icon": "toolbox",
                "title": "AI Tools",
                "label": "AI Tools",
                "description": "Use purpose-built workflows for image/video creation, writing, analysis, code, business, and media editing.",
                "href": "/docs/tools",
                "color": "#C2410C",
                "endpoint": ""
              },
              {
                "icon": "pen-to-square",
                "title": "Docs, Slides & Sheets",
                "label": "Docs, Slides & Sheets",
                "description": "Create documents, presentations, and spreadsheets in a live editor beside the chat — then export to PDF, Word, PowerPoint, or Excel.",
                "href": "/docs/editors",
                "color": "#6366F1",
                "endpoint": ""
              },
              {
                "icon": "image",
                "title": "Image Generation",
                "label": "Image Generation",
                "description": "Create stunning AI images with GPT Image 2, Nano Banana 2, Seedream, Qwen Image, and more.",
                "href": "/docs/images",
                "color": "#EC4899",
                "endpoint": ""
              },
              {
                "icon": "video",
                "title": "Video Generation",
                "label": "Video Generation",
                "description": "Generate AI videos from text or an image using Veo 3.1, Kling, Hailuo, Wan, and Seedance.",
                "href": "/docs/videos",
                "color": "#F59E0B",
                "endpoint": ""
              },
              {
                "icon": "music",
                "title": "Music Generation",
                "label": "Music Generation",
                "description": "Create original songs with lyrics, vocals, and instrumentals powered by MiniMax Music.",
                "href": "/docs/music",
                "color": "#10B981",
                "endpoint": ""
              },
              {
                "icon": "cube",
                "title": "3D & Avatars",
                "label": "3D & Avatars",
                "description": "Generate 3D models from text or images, and create lifelike talking-avatar videos.",
                "href": "/docs/models",
                "color": "#A855F7",
                "endpoint": ""
              },
              {
                "icon": "globe",
                "title": "Web Search",
                "label": "Web Search",
                "description": "Get real-time information from the web integrated directly into AI responses.",
                "href": "/docs/web-search",
                "color": "#06B6D4",
                "endpoint": ""
              },
              {
                "icon": "brain",
                "title": "AI Memory",
                "label": "AI Memory",
                "description": "Personalized memory that carries across every model so your AI actually knows you.",
                "href": "/docs/memory",
                "color": "#8B5CF6",
                "endpoint": ""
              },
              {
                "icon": "robot",
                "title": "Custom Bots",
                "label": "Custom Bots",
                "description": "Build and share specialized AI assistants with their own instructions, models, and knowledge.",
                "href": "/docs/agents",
                "color": "#6366F1",
                "endpoint": ""
              },
              {
                "icon": "bookmark",
                "title": "Bookmarks",
                "label": "Bookmarks",
                "description": "Save important AI messages and organize them into folders so you can find them later.",
                "href": "/docs/bookmarks",
                "color": "#F97316",
                "endpoint": ""
              },
              {
                "icon": "folder",
                "title": "Collections",
                "label": "Collections",
                "description": "Group related chats and files by project, topic, or client with shared instructions.",
                "href": "/docs/collections",
                "color": "#3B82F6",
                "endpoint": ""
              },
              {
                "icon": "file",
                "title": "Files",
                "label": "Files",
                "description": "Upload PDFs, documents, and images and chat with them using any model.",
                "href": "/docs/files",
                "color": "#14B8A6",
                "endpoint": ""
              },
              {
                "icon": "clock",
                "title": "Automated Tasks",
                "label": "Automated Tasks",
                "description": "Schedule AI to run on a recurring basis—daily briefings, reminders, and monitoring.",
                "href": "/docs/tasks",
                "color": "#0EA5E9",
                "endpoint": ""
              },
              {
                "icon": "file-import",
                "title": "Import Chats",
                "label": "Import Chats",
                "description": "Bring over your chat history from Gab AI, ChatGPT, Claude, and Grok in a few clicks.",
                "href": "/docs/import-chats",
                "color": "#A855F7",
                "endpoint": ""
              },
              {
                "icon": "box-archive",
                "title": "Download Your Data",
                "label": "Download Your Data",
                "description": "Export a complete ZIP archive of your account anytime — chats, memories, files, bots, collections, and more. Excludes deleted and temporary chats.",
                "href": "/docs/data-export",
                "color": "#0EA5E9",
                "endpoint": ""
              },
              {
                "icon": "code",
                "title": "Developer API",
                "label": "Developer API",
                "description": "OpenAI-compatible API for building on top of Gab AI with your own tools.",
                "href": "/docs/api",
                "color": "#10B981",
                "endpoint": ""
              }
            ]
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "Why Choose Gab AI?",
            "items": [
              "Work — complete projects with an AI that can research, create files, code, and produce media",
              "Voice Mode — Talk to AI naturally with real-time voice conversations",
              "AI Tools — Purpose-built workflows for creation, editing, writing, analysis, code, and business",
              "One subscription for all AI capabilities—no more juggling multiple services",
              "Intelligent routing automatically selects the best model for your task",
              "Personalized AI memory that learns your preferences over time",
              "Bookmarks and Collections to save and organize your best conversations",
              "Import your chat history from Gab AI, ChatGPT, Claude, and Grok",
              "Controls for saved memories, conversation history, connected accounts, and file access",
              "Built on American values, independent from Big Tech control",
              "Developer-friendly API with OpenAI compatibility"
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Privacy First",
            "copy": "Gab AI sends requests to supported model and service providers to produce your results. Prompts, attachments, and enabled memory or project context may contain personal information. Review the Privacy Policy and choose what you include or connect."
          },
          {
            "type": "quick-links",
            "title": "Explore the Documentation",
            "items": [
              {
                "icon": "rocket",
                "title": "Quickstart",
                "label": "Quickstart",
                "description": "Get started in minutes",
                "href": "/docs/quickstart",
                "color": "#3B82F6",
                "endpoint": ""
              },
              {
                "icon": "microphone",
                "title": "Voice Mode",
                "label": "Voice Mode",
                "description": "Real-time voice conversations",
                "href": "/docs/voice",
                "color": "#8B5CF6",
                "endpoint": ""
              },
              {
                "icon": "toolbox",
                "title": "AI Tools",
                "label": "AI Tools",
                "description": "Guided AI workflows",
                "href": "/docs/tools",
                "color": "#C2410C",
                "endpoint": ""
              },
              {
                "icon": "microchip",
                "title": "AI Models",
                "label": "AI Models",
                "description": "Available models & routing",
                "href": "/docs/models",
                "color": "#10B981",
                "endpoint": ""
              },
              {
                "icon": "brain",
                "title": "AI Memory",
                "label": "AI Memory",
                "description": "One memory across every model",
                "href": "/docs/memory",
                "color": "#8B5CF6",
                "endpoint": ""
              },
              {
                "icon": "bookmark",
                "title": "Bookmarks",
                "label": "Bookmarks",
                "description": "Save and organize messages",
                "href": "/docs/bookmarks",
                "color": "#F97316",
                "endpoint": ""
              },
              {
                "icon": "coins",
                "title": "Credits System",
                "label": "Credits System",
                "description": "How credits work",
                "href": "/docs/credits",
                "color": "#F59E0B",
                "endpoint": ""
              }
            ]
          },
          {
            "type": "section",
            "title": "Supported AI Models",
            "copy": "Gab AI provides access to the world's leading AI models through a single interface:"
          },
          {
            "type": "unordered-list",
            "items": [
              "GPT-5.6 Sol",
              "GPT-5.6 Terra",
              "GPT-5.3 Codex",
              "Claude Opus 5",
              "Claude Sonnet 5",
              "Claude Haiku 4.5",
              "Gemini 3.1 Pro",
              "Gemini 3.6 Flash",
              "DeepSeek V4",
              "Kimi K3",
              "Qwen 3.7",
              "GLM 5.2",
              "Hermes 4",
              "Arya"
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Arya AI",
            "copy": "Arya is our exclusive AI built with Christian values and conservative principles. It also powers our intelligent router—when you send a message without picking a model, Arya detects your intent and routes you to the best model for the job (chat, image, video, music, web search, reasoning, and more). Arya is included free for Plus subscribers with no credit cost."
          },
          {
            "type": "section",
            "title": "Getting Help",
            "copy": "If you need assistance or have questions:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Browse this documentation for guides and tutorials",
              "Contact support at support@gab.ai",
              "Check our YouTube channel for video tutorials",
              "Join the Gab community at gab.com"
            ]
          },
          {
            "type": "quick-links",
            "title": "Gab AI Business",
            "items": [
              {
                "title": "Gab AI Business",
                "href": "/docs/business",
                "description": "Company billing, Plus and Plus Premium seats, and shared credits."
              },
              {
                "title": "Existing accounts and Business",
                "href": "/docs/business-upgrade",
                "description": "Web setup for existing users, including Apple and Google subscribers."
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/da0db04f-64a4-4eaf-8a26-04baed1e8a27.png"
      },
      "guideSeo": {},
      "navigation": {
        "next": {
          "label": "Quickstart",
          "href": "/docs/quickstart"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/index",
      "updatedAt": "2026-09-15T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "quickstart",
      "title": "Quickstart",
      "description": "Get up and running with Gab AI in just a few minutes. This guide will walk you through creating your account and starting your first AI conversation.",
      "badge": "Getting Started",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "getting-started",
      "navOrder": 1,
      "navIcon": "rocket",
      "searchKeywords": [
        "quick",
        "fast",
        "setup",
        "guide",
        "tutorial"
      ],
      "meta": {
        "title": "Gab AI Quickstart: Start Chatting with AI in Minutes",
        "description": "Start using Gab AI in under 5 minutes. Create an account, send your first AI prompt, generate an image, and try real-time voice mode in one short walkthrough.",
        "keywords": "Gab AI quickstart, getting started AI, AI tutorial beginner, first AI prompt, AI setup guide, sign up Gab AI, AI chatbot getting started",
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "How to create your Gab AI account",
              "Starting your first AI conversation",
              "Navigating the interface and key features",
              "Upgrading to Plus for free default model messages in the app"
            ],
            "readTime": 3,
            "prerequisites": [
              {
                "label": "Introduction",
                "href": "/docs"
              }
            ]
          },
          {
            "type": "section",
            "title": "Verify your account and enter Chat",
            "copy": "Free email/password signups must verify their email before entering the app. Use the verification screen to resend the message or check again after opening your inbox. Apple/Google signups with a provider-verified address and current Plus accounts follow their account’s verification exemption; an existing MFA challenge still applies. There is no required onboarding questionnaire, product tour, or onboarding paywall."
          },
          {
            "type": "section",
            "title": "Before You Begin",
            "copy": "To get started with Gab AI, create an account and use a modern web browser or a Gab AI native app. Sign in with the same account on each device to use your conversations, plan, and credits.\n\nFor Work with folders on your computer, use the Mac app. To continue those folder tasks on iOS or Android, pair your phone with the Mac using Remote. Local and Remote folder tasks require a plan with Work access."
          },
          {
            "type": "steps",
            "title": "Getting Started",
            "items": [
              {
                "title": "Create Your Account",
                "copy": "Visit gab.ai and click \"Get Started\" to create your free account. You can sign up with your email address. You'll get 250 free credits to explore the platform (30 up front, and the rest once you verify your email)."
              },
              {
                "title": "Start Your First Chat",
                "copy": "Once signed in, you'll land on the main chat interface. Simply type your message in the input field and press Enter. Our intelligent routing will automatically select the best AI model for your request."
              },
              {
                "title": "Try Different Features",
                "copy": "Explore the sidebar to discover all features: generate images, create videos, make music, upload files, and more. Each feature is accessible directly from the main navigation."
              },
              {
                "title": "Upgrade to Plus (Optional)",
                "copy": "The free tier has limited messages. Upgrade to Plus for free default model messages in the app (not included in API), 2,000 monthly credits for other models, and premium features like AI Memory."
              }
            ]
          },
          {
            "type": "section",
            "title": "Your First Chat",
            "copy": "The chat interface is your main workspace. Here's what you can do:\n\nTry these example prompts to explore what Gab AI can do:"
          },
          {
            "type": "code",
            "code": "💬 Chat: \"Explain quantum computing like I'm 10 years old\"\n\n🖼️ Image: \"Generate an image of a sunset over mountains\"\n\n🎵 Music: \"Create an upbeat pop song about summer\"\n\n🔍 Search: \"What are the latest news about AI?\"\n\n📄 Code: \"Write a Python function to sort a list\"",
            "language": "text",
            "title": "Example Prompts"
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Pro Tip",
            "copy": "You don't need to specify which AI model to use. Just describe what you need, and our intelligent routing will pick the best model automatically."
          },
          {
            "type": "section",
            "title": "Understanding the Interface",
            "copy": "The Gab AI interface is designed to be intuitive:\n\nSidebar (Left) — Navigate between features: Chat, Images, Videos, Music, Bots, Collections, Files, and Settings.\n\nChat Area (Center) — Your conversation history with the AI. Messages are grouped by conversation.\n\nInput Bar (Bottom) — Type your messages here. Use the attachment button to upload files or images.\n\nModel Selector — Choose a specific AI model or let auto-routing decide for you."
          },
          {
            "type": "section",
            "title": "Set up the native apps",
            "copy": "Use the Mac app guide for installation and your first local Work task. Choose Project folder before sending a new Work chat to connect files on your Mac. On your phone, install the Gab AI iOS or Android app and follow Remote setup to pair it with the Mac."
          },
          {
            "type": "quick-links",
            "title": "Mac app and Remote guides",
            "items": [
              {
                "label": "Get started with the Mac app",
                "href": "/docs/mac-app"
              },
              {
                "label": "Project folders and permissions",
                "href": "/docs/project-folders"
              },
              {
                "label": "Set up Remote on iOS or Android",
                "href": "/docs/remote"
              }
            ]
          },
          {
            "type": "section",
            "title": "Next Steps",
            "copy": "Now that you're set up, explore these topics to get the most out of Gab AI:"
          },
          {
            "type": "quick-links",
            "items": [
              {
                "icon": "microchip",
                "title": "AI Models",
                "label": "AI Models",
                "description": "Learn about available models",
                "href": "/docs/models",
                "color": "#8B5CF6",
                "endpoint": ""
              },
              {
                "icon": "image",
                "title": "Image Generation",
                "label": "Image Generation",
                "description": "Create AI artwork",
                "href": "/docs/images",
                "color": "#EC4899",
                "endpoint": ""
              },
              {
                "icon": "robot",
                "title": "Custom Bots",
                "label": "Custom Bots",
                "description": "Build AI assistants",
                "href": "/docs/agents",
                "color": "#3B82F6",
                "endpoint": ""
              },
              {
                "icon": "brain",
                "title": "AI Memory",
                "label": "AI Memory",
                "description": "Personalize your AI",
                "href": "/docs/memory",
                "color": "#10B981",
                "endpoint": ""
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/c46e3cec-875f-44bb-a71d-bcd3b5bd49b1.png"
      },
      "guideSeo": {
        "readMinutes": 4,
        "difficulty": "Beginner",
        "articleSection": "Getting Started",
        "about": [
          "Getting started with AI",
          "First AI prompt"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Welcome to Gab AI",
          "href": "/docs"
        },
        "next": {
          "label": "Platform Overview",
          "href": "/docs/overview"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/quickstart",
      "updatedAt": "2026-09-15T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "overview",
      "title": "Platform Overview",
      "description": "Understand how Gab AI brings together multiple AI models into one seamless experience with intelligent routing and personalization.",
      "badge": "Core Concepts",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "core-concepts",
      "navOrder": 2,
      "navIcon": "circle-info",
      "searchKeywords": [
        "platform",
        "how",
        "works",
        "architecture"
      ],
      "meta": {
        "title": "How Gab AI Works: Intelligent Routing & Unified AI Access",
        "description": "How Gab AI unifies the world's best AI models behind one account. Intelligent routing, multimodal generation, and a single subscription that replaces five — see how it fits together.",
        "keywords": "how Gab AI works, intelligent AI routing, unified AI, all-in-one AI, multimodal AI platform, AI model router, ChatGPT alternative, AI orchestration",
        "pageCopy": [
          {
            "type": "section",
            "title": "The Problem We Solve",
            "copy": "Today's AI landscape is fragmented. You might use ChatGPT for writing, Midjourney for images, Suno for music, and various other tools—each requiring separate subscriptions, different interfaces, and scattered data.\n\nGab AI solves this by unifying all top AI models into a single platform with one subscription. You get the best of every AI capability without the complexity."
          },
          {
            "type": "section",
            "title": "How It Works",
            "copy": "When you send a message to Gab AI, our intelligent routing system analyzes your request and automatically selects the most suitable AI model. Watch how it works:\n\nYou can also manually choose a specific model if you prefer using the model selector in the chat interface."
          },
          {
            "type": "section",
            "title": "Intelligent Routing",
            "copy": "Our intelligent routing system analyzes each request and considers:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Task type — coding, writing, analysis, creative work, etc.",
              "Complexity — simple questions vs. complex reasoning",
              "Context — conversation history and attached files",
              "Speed requirements — quick responses vs. thorough analysis"
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "You're Always in Control",
            "copy": "While auto-routing works great for most cases, you can always select a specific model using the model selector in the chat interface."
          },
          {
            "type": "section",
            "title": "Privacy Architecture",
            "copy": "Gab AI routes model requests through its services. The selected provider receives the content needed to answer, which can include prompts, attachments, and enabled memory or project context. Do not assume personal information written in that content is automatically removed. The Privacy Policy describes applicable data-handling terms."
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "user-shield",
                "title": "Provider requests",
                "subtitle": "",
                "description": "Gab AI uses its provider integrations to process requests. Personal information included in prompts or context can reach the selected provider.",
                "color": "#10B981"
              },
              {
                "icon": "shield-halved",
                "title": "Data-handling terms",
                "subtitle": "",
                "description": "Read the Privacy Policy for information about processing, providers, retention, and your choices.",
                "color": "#3B82F6"
              },
              {
                "icon": "lock",
                "title": "Access controls",
                "subtitle": "",
                "description": "Choose which files, accounts, websites, and apps a task can access. Installing a plugin does not grant those permissions.",
                "color": "#8B5CF6"
              }
            ]
          },
          {
            "type": "section",
            "title": "Platform Capabilities",
            "copy": "Gab AI isn't just a chatbot—it's a complete AI platform with capabilities spanning:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Text Generation — Writing, coding, analysis, and conversation",
              "Image Generation — Create art, photos, and graphics",
              "Video Generation — AI-powered video creation",
              "Music Generation — Songs, instrumentals, and sound design",
              "3D Generation — Turn text or images into 3D models",
              "Talking Avatars — Lifelike avatar videos from a portrait and script",
              "Voice Mode — Real-time voice conversations",
              "Web Search — Real-time information from the internet",
              "File Analysis — Upload and chat with documents",
              "Custom Bots — Build specialized AI assistants"
            ]
          }
        ],
        "ogImage": "guides/social-cards/0703d9f8-21e9-497d-8eef-fa9768ee2c1c.png"
      },
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Getting Started",
        "about": [
          "AI platform overview",
          "Intelligent routing",
          "Unified AI access"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Quickstart",
          "href": "/docs/quickstart"
        },
        "next": {
          "label": "AI Models",
          "href": "/docs/models"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/overview"
    },
    {
      "guideType": "docs",
      "slug": "models",
      "title": "AI Models",
      "description": "Gab AI gives you access to the world's leading AI models through a single interface. Learn about each model's strengths and when to use them.",
      "badge": "Core Concepts",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "core-concepts",
      "navOrder": 3,
      "navIcon": "microchip",
      "searchKeywords": [
        "gpt",
        "claude",
        "gemini",
        "arya",
        "llm",
        "language model",
        "deepseek"
      ],
      "meta": {
        "title": "AI Models on Gab AI: GPT-5.5, Claude, Gemini, Veo & More",
        "description": "Compare every AI model in Gab AI: GPT-5.5, Claude Opus 4.8, Gemini 3.1 Pro, DeepSeek V4, Kimi K2.6, Qwen, Veo 3.1, Kling, plus image, video, music, 3D, and our exclusive Arya — strengths, costs, and ideal use cases.",
        "keywords": "AI models comparison, GPT-5.5, Claude Opus 4.8, Gemini 3.1 Pro, DeepSeek V4, Kimi K2.6, Qwen 3.5, Veo 3.1, Kling video AI, Hailuo, Wan, 3D generation, Arya AI, LLM comparison, multi-model AI",
        "pageCopy": [
          {
            "type": "section",
            "title": "How Model Selection Works",
            "copy": "By default, Gab AI uses intelligent routing to automatically select the best model for your request. You can also manually select a model using the model selector in the chat interface.\n\nOn the API, model \"auto\" is a router id, not a priced model. Coding work is classified as quick, standard, or hard. Credits follow the slug that actually ran. The model mapped to each coding tier is configurable and can change — it is not a stable API contract. Inspect X-Gab-Routed-Model (or the response model field) for the engine that ran. Pin a specific model id when you need a guaranteed engine."
          },
          {
            "type": "section",
            "title": "Check the current catalog",
            "copy": "The app’s Models page and model selector show the models currently enabled for your account. The catalog includes newer GPT-6 and GPT Image 2.5 options where available; older examples below are not a complete inventory. Availability, parameters, and credits can change. Developers should use GET /v1/models and pin an explicit available model id when they need a specific engine."
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Default AI is Free in the App",
            "copy": "Our default AI model is free for Plus subscribers when chatting in Gab AI. Not included in API — API requests always use credits. Other third-party models use credits based on usage."
          },
          {
            "type": "section",
            "title": "Text & Chat Models",
            "copy": "These models power conversations, writing, coding, and analysis:"
          },
          {
            "type": "cards",
            "items": [
              {
                "title": "Arya 3",
                "subtitle": "Gab AI",
                "description": "Gab AI's core model. Free for Plus subscribers in the app (not included in api).\nStrengths: General conversation, Writing assistance, Value-aligned responses",
                "color": "#EC4899"
              },
              {
                "title": "Claude Opus 5",
                "subtitle": "Anthropic",
                "description": "Anthropic's most capable Opus model — near-frontier intelligence for coding, bots, and knowledge work, with a 1M-token context window.\nStrengths: Agentic coding, Complex reasoning, Analysis",
                "color": "#F59E0B"
              },
              {
                "title": "Claude Opus 4.8",
                "subtitle": "Anthropic",
                "description": "Anthropic's most capable model with frontier reasoning and a step-change improvement in agentic coding.\nStrengths: Agentic coding, Complex reasoning, Analysis",
                "color": "#F59E0B"
              },
              {
                "title": "Claude Opus 4.7",
                "subtitle": "Anthropic",
                "description": "Premium model combining maximum intelligence with practical performance.\nStrengths: Complex reasoning, Analysis, Creative writing",
                "color": "#F59E0B"
              },
              {
                "title": "Claude Sonnet 5",
                "subtitle": "Anthropic",
                "description": "Anthropic's most capable Sonnet-class model — frontier performance across coding, bots, and professional work, with adaptive thinking and a 1M-token context.\nStrengths: Coding, Agentic workflows, Professional work",
                "color": "#F59E0B"
              },
              {
                "title": "Claude Sonnet 4.6",
                "subtitle": "Anthropic",
                "description": "Hybrid Sonnet excels at coding, data analysis, and planning with fast, efficient responses.\nStrengths: Coding, Data analysis, Planning",
                "color": "#F59E0B"
              },
              {
                "title": "Claude Sonnet 4.5",
                "subtitle": "Anthropic",
                "description": "Balanced Sonnet model strong at coding, data analysis, and everyday reasoning.\nStrengths: Coding, Data analysis, Reasoning",
                "color": "#F59E0B"
              },
              {
                "title": "Claude Haiku 4.5",
                "subtitle": "Anthropic",
                "description": "Fastest, most cost-efficient Anthropic model.\nStrengths: Speed, Efficiency, Quick tasks",
                "color": "#F59E0B"
              },
              {
                "title": "GPT-5.6 Sol",
                "subtitle": "OpenAI",
                "description": "The flagship of OpenAI's GPT-5.6 series — built for complex reasoning, coding, and agentic workflows.\nStrengths: Advanced reasoning, Coding, Long-horizon tasks",
                "color": "#10B981"
              },
              {
                "title": "GPT-5.6 Sol Pro",
                "subtitle": "OpenAI",
                "description": "GPT-5.6 Sol in pro reasoning mode — deeper reasoning for the hardest coding, analysis, and agentic tasks.\nStrengths: Deep reasoning, Complex problem solving, Coding",
                "color": "#10B981"
              },
              {
                "title": "GPT-5.6 Terra",
                "subtitle": "OpenAI",
                "description": "The balanced model in OpenAI's GPT-5.6 series — everyday coding, reasoning, and agentic tasks at roughly half the flagship cost.\nStrengths: Everyday coding, Reasoning, Price-performance",
                "color": "#10B981"
              },
              {
                "title": "GPT-5.5",
                "subtitle": "OpenAI",
                "description": "OpenAI's flagship general-purpose model with strong reasoning and tool use.\nStrengths: General reasoning, Tool use, Writing",
                "color": "#10B981"
              },
              {
                "title": "GPT-5.3 Codex",
                "subtitle": "OpenAI",
                "description": "OpenAI's most advanced agentic coding model, combining frontier software engineering performance with broader reasoning.\nStrengths: Code generation, Software engineering, Reasoning",
                "color": "#10B981"
              },
              {
                "title": "o3",
                "subtitle": "OpenAI",
                "description": "OpenAI's deep reasoning model for math, science, and complex multi-step problems.\nStrengths: Deep reasoning, Math, Science",
                "color": "#10B981"
              },
              {
                "title": "Gemini 3.1 Pro",
                "subtitle": "Google",
                "description": "One of the best models in the world for multimodal understanding, and Google's most powerful agentic and vibe-coding model.\nStrengths: Multi-modal, Agentic, Coding",
                "color": "#3B82F6"
              },
              {
                "title": "Gemini 3.6 Flash",
                "subtitle": "Google",
                "description": "Google's frontier-level Flash model for the agentic era — code generation, agentic execution, and spatial reasoning at high speed and low cost.\nStrengths: Coding, Agentic execution, Cost efficiency",
                "color": "#3B82F6"
              },
              {
                "title": "Gemini 3.5 Flash",
                "subtitle": "Google",
                "description": "Fast, balanced Gemini model strong at reasoning, coding, and everyday multimodal tasks.\nStrengths: Reasoning, Coding, Speed",
                "color": "#3B82F6"
              },
              {
                "title": "Gemini 3.1 Flash Lite",
                "subtitle": "Google",
                "description": "Lightweight Gemini model tuned for low-latency responses and real-time web access.\nStrengths: Speed, Web search, Cost-efficient",
                "color": "#3B82F6"
              },
              {
                "title": "DeepSeek V4 Pro",
                "subtitle": "DeepSeek",
                "description": "DeepSeek's top-tier model for advanced reasoning, math, and coding tasks.\nStrengths: Reasoning, Coding, Math",
                "color": "#06B6D4"
              },
              {
                "title": "DeepSeek V4 Flash",
                "subtitle": "DeepSeek",
                "description": "Faster, cost-efficient DeepSeek variant for everyday text and code generation.\nStrengths: Speed, Cost-efficient, Coding",
                "color": "#06B6D4"
              },
              {
                "title": "GLM 5.2",
                "subtitle": "Z.ai",
                "description": "Z.ai's flagship for long-horizon tasks — a usable 1M-token context that carries project-level work from requirements to deployment.\nStrengths: Long-horizon planning, Coding, Execution",
                "color": "#8B5CF6"
              },
              {
                "title": "GLM 5.1",
                "subtitle": "Z.ai",
                "description": "Z.ai's flagship model — strong text generation and coding with multilingual support.\nStrengths: Text generation, Coding, Multilingual",
                "color": "#8B5CF6"
              },
              {
                "title": "Qwen 3.7 Max",
                "subtitle": "Alibaba",
                "description": "Alibaba's top-tier Qwen model for advanced reasoning, coding, and agentic tasks.\nStrengths: Reasoning, Coding, Agentic tasks",
                "color": "#8B5CF6"
              },
              {
                "title": "Qwen 3.7 Plus",
                "subtitle": "Alibaba",
                "description": "Cost-effective Qwen3.7 model with vision-language understanding, coding, and agentic tool use.\nStrengths: Vision-language, Coding, Cost-efficient",
                "color": "#8B5CF6"
              },
              {
                "title": "Qwen 3.5 397B Thinking",
                "subtitle": "Alibaba",
                "description": "Thinking variant of Qwen 3.5 397B — strengthens chain-of-thought for harder analytical tasks.\nStrengths: Advanced reasoning, Math, Problem-solving",
                "color": "#8B5CF6"
              },
              {
                "title": "Kimi K3",
                "subtitle": "Moonshot",
                "description": "Moonshot AI's open-weight multimodal reasoning model for complex coding, knowledge work, and long-horizon agentic workflows.\nStrengths: Complex coding, Multimodal reasoning, Long context",
                "color": "#06B6D4"
              },
              {
                "title": "Kimi K2.6",
                "subtitle": "Moonshot",
                "description": "Moonshot AI's next-generation multimodal model for long-horizon coding, UI/UX generation, and multi-bot orchestration.\nStrengths: Long-horizon coding, Multimodal, Bot orchestration",
                "color": "#06B6D4"
              },
              {
                "title": "Kimi K2 Thinking",
                "subtitle": "Moonshot",
                "description": "Moonshot AI's most advanced open reasoning model, extending the K2 series into agentic, long-horizon reasoning.\nStrengths: Agentic reasoning, Long-horizon tasks, Open source",
                "color": "#06B6D4"
              },
              {
                "title": "MiniMax M3",
                "subtitle": "MiniMax",
                "description": "Latest MiniMax model — strong coding and reasoning with efficient inference cost.\nStrengths: Coding, Reasoning, Cost-efficient",
                "color": "#F97316"
              },
              {
                "title": "Hermes 4 405B",
                "subtitle": "Nous Research",
                "description": "The largest model in the Hermes 4 family — the fullest expression of Nous Research design, focused on advanced reasoning.\nStrengths: Advanced reasoning, Instruction following, Open source",
                "color": "#EF4444"
              },
              {
                "title": "Gemma 4 26B",
                "subtitle": "Google",
                "description": "Open-weights Gemma model — capable general-purpose chat with strong instruction following.\nStrengths: Open-weights, Instruction following, Cost-efficient",
                "color": "#3B82F6"
              }
            ]
          },
          {
            "type": "section",
            "title": "Image Generation Models",
            "copy": "Create stunning AI-generated images with these models:"
          },
          {
            "type": "cards",
            "items": [
              {
                "title": "GPT Image 2",
                "subtitle": "OpenAI",
                "description": "OpenAI's state-of-the-art image generation and editing model with high-fidelity image inputs and flexible sizes.",
                "color": "#10B981"
              },
              {
                "title": "GPT Image 1 Mini",
                "subtitle": "OpenAI",
                "description": "Faster, cost-efficient OpenAI image model that accepts both text and image inputs.",
                "color": "#10B981"
              },
              {
                "title": "Nano Banana 2",
                "subtitle": "Google",
                "description": "Google's fast, high-quality image generation and editing model.",
                "color": "#3B82F6"
              },
              {
                "title": "Seedream 4.5",
                "subtitle": "ByteDance",
                "description": "Latest Seedream — unified image generation and editing with sharper text rendering.",
                "color": "#8B5CF6"
              },
              {
                "title": "Seedream 4.0",
                "subtitle": "ByteDance",
                "description": "Integrates image generation and image editing capabilities in a single, unified architecture.",
                "color": "#8B5CF6"
              },
              {
                "title": "Qwen Image 2",
                "subtitle": "Alibaba",
                "description": "Latest Qwen image model — strong text rendering and precise instruction following.",
                "color": "#8B5CF6"
              },
              {
                "title": "Qwen Image",
                "subtitle": "Alibaba",
                "description": "A powerful image generation model capable of complex text rendering and precise image editing.",
                "color": "#8B5CF6"
              },
              {
                "title": "Wan 2.7 Image",
                "subtitle": "Alibaba",
                "description": "Alibaba WAN image model for high-quality generation and editing from text or reference images.",
                "color": "#8B5CF6"
              },
              {
                "title": "Image Generator",
                "subtitle": "Gab AI",
                "description": "Generate vivid, realistic images based on a text prompt.",
                "color": "#EC4899"
              }
            ]
          },
          {
            "type": "section",
            "title": "Video Generation",
            "copy": "Generate AI videos from text descriptions:"
          },
          {
            "type": "cards",
            "items": [
              {
                "title": "Veo 3.1",
                "subtitle": "Google",
                "description": "Google's state-of-the-art model for generating high-fidelity videos.",
                "color": "#3B82F6"
              },
              {
                "title": "Veo 3.1 Fast",
                "subtitle": "Google",
                "description": "Faster and more cost effective version of Google's Veo 3.1.",
                "color": "#3B82F6"
              },
              {
                "title": "Veo 3.1 Img2Vid",
                "subtitle": "Google",
                "description": "Google's state-of-the-art model for generating high-fidelity videos from images.",
                "color": "#3B82F6"
              },
              {
                "title": "Kling 3 Pro",
                "subtitle": "Kling",
                "description": "Top-tier motion video generation with exceptional prompt precision.",
                "color": "#F59E0B"
              },
              {
                "title": "Kling 3.0 Img2Vid",
                "subtitle": "Kling",
                "description": "Top-tier motion video generation from images with exceptional prompt precision.",
                "color": "#F59E0B"
              },
              {
                "title": "Kling 2.5 Turbo Pro",
                "subtitle": "Kling",
                "description": "Top-tier video generation with exceptional prompt precision.",
                "color": "#F59E0B"
              },
              {
                "title": "Kling 2.5 Turbo Pro Img2Vid",
                "subtitle": "Kling",
                "description": "Top-tier video generation from images with exceptional prompt precision.",
                "color": "#F59E0B"
              },
              {
                "title": "Hailuo 2.3 Pro",
                "subtitle": "MiniMax",
                "description": "Advanced text-to-video generation model with 1080p resolution.",
                "color": "#F97316"
              },
              {
                "title": "Hailuo 2.3",
                "subtitle": "MiniMax",
                "description": "Advanced text-to-video generation model with 1080p resolution.",
                "color": "#F97316"
              },
              {
                "title": "Wan 2.7",
                "subtitle": "Alibaba",
                "description": "Latest Alibaba WAN — generates high-quality AI videos with audio from text or images.",
                "color": "#8B5CF6"
              },
              {
                "title": "Wan 2.5",
                "subtitle": "Alibaba",
                "description": "Generate high-quality AI videos with audio using Alibaba WAN 2.5.",
                "color": "#8B5CF6"
              },
              {
                "title": "Seedance 2.0",
                "subtitle": "ByteDance",
                "description": "ByteDance video model with strong motion coherence for both text-to-video and image-to-video.",
                "color": "#8B5CF6"
              }
            ]
          },
          {
            "type": "section",
            "title": "Music Generation",
            "copy": "Create original music and songs with AI:"
          },
          {
            "type": "cards",
            "items": [
              {
                "title": "MiniMax Music 2.6",
                "subtitle": "MiniMax",
                "description": "Latest MiniMax music model — generates high-quality, diverse musical compositions from text prompts.",
                "color": "#F97316"
              },
              {
                "title": "MiniMax Music 2.0",
                "subtitle": "MiniMax",
                "description": "Generate high-quality, diverse musical compositions from text prompts.",
                "color": "#F97316"
              }
            ]
          },
          {
            "type": "section",
            "title": "3D Generation",
            "copy": "Turn text prompts or images into downloadable 3D models:"
          },
          {
            "type": "cards",
            "items": [
              {
                "title": "Hunyuan 3D v3.1 Pro",
                "subtitle": "Tencent",
                "description": "High-fidelity 3D model generation from a text prompt or an image.",
                "color": "#06B6D4"
              },
              {
                "title": "Hunyuan 3D v3.1 Rapid",
                "subtitle": "Tencent",
                "description": "Faster Hunyuan 3D generation for quick iterations from text or an image.",
                "color": "#06B6D4"
              },
              {
                "title": "Tripo H3.1",
                "subtitle": "Tripo3D",
                "description": "Generate detailed, production-ready 3D meshes from a text prompt.",
                "color": "#8B5CF6"
              },
              {
                "title": "Meshy v6",
                "subtitle": "Meshy",
                "description": "Create textured 3D models from text or an image.",
                "color": "#F59E0B"
              }
            ]
          },
          {
            "type": "section",
            "title": "Talking Avatars",
            "copy": "Generate lifelike talking-avatar videos from a portrait, script, or audio:"
          },
          {
            "type": "cards",
            "items": [
              {
                "title": "HeyGen Avatar 4",
                "subtitle": "HeyGen",
                "description": "Generate lifelike talking-avatar videos from a portrait and a script.",
                "color": "#10B981"
              },
              {
                "title": "HeyGen Video Bot",
                "subtitle": "HeyGen",
                "description": "Create talking-avatar videos driven by your text or audio.",
                "color": "#10B981"
              },
              {
                "title": "Kling Avatar v2 Pro",
                "subtitle": "Kling",
                "description": "High-quality talking and singing avatar videos from an image and audio.",
                "color": "#F59E0B"
              },
              {
                "title": "Hunyuan Avatar",
                "subtitle": "Tencent",
                "description": "Animate a portrait into a talking avatar driven by audio.",
                "color": "#06B6D4"
              },
              {
                "title": "MultiTalk Avatar",
                "subtitle": "Fal AI",
                "description": "Lip-synced talking-avatar video generation from audio or text-to-speech.",
                "color": "#EC4899"
              }
            ]
          },
          {
            "type": "section",
            "title": "Speech & Sound Effects",
            "copy": "Convert text to natural speech and generate sound effects:"
          },
          {
            "type": "cards",
            "items": [
              {
                "title": "GPT-4o mini TTS",
                "subtitle": "OpenAI",
                "description": "Natural text-to-speech with a range of expressive voices.",
                "color": "#10B981"
              },
              {
                "title": "ElevenLabs Sound Effects V2",
                "subtitle": "ElevenLabs",
                "description": "Generate sound effects and short audio clips from a text description.",
                "color": "#EC4899"
              },
              {
                "title": "CassetteAI Sound Effects",
                "subtitle": "CassetteAI",
                "description": "Fast text-to-sound-effect generation.",
                "color": "#F97316"
              }
            ]
          },
          {
            "type": "section",
            "title": "Model Availability",
            "copy": "We're constantly adding new models as they become available. Model availability may vary based on your subscription tier:"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Free Tier",
                "description": "Limited access to basic models"
              },
              {
                "icon": "",
                "title": "Plus",
                "description": "All models + free default model messages + 2,000 credits/mo"
              },
              {
                "icon": "",
                "title": "Enterprise",
                "description": "Custom model access + volume pricing"
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/1156eac3-855a-42c0-a145-8bb3e39f42ad.png"
      },
      "guideSeo": {
        "readMinutes": 8,
        "difficulty": "Beginner",
        "articleSection": "Models",
        "about": [
          "AI models",
          "Large language models",
          "Generative video models",
          "Image generation models",
          "3D generation models"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Platform Overview",
          "href": "/docs/overview"
        },
        "next": {
          "label": "Credits System",
          "href": "/docs/credits"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/models"
    },
    {
      "guideType": "docs",
      "slug": "pricing-guide",
      "title": "Pricing",
      "description": "Start free, or unlock agentic Work mode and premium AI capabilities with Plus.",
      "badge": "Reference",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "core-concepts",
      "navOrder": 5,
      "navIcon": "tag",
      "searchKeywords": [
        "price",
        "plan",
        "subscription",
        "cost",
        "work mode",
        "agentic AI",
        "enterprise"
      ],
      "meta": {
        "title": "Gab AI Pricing: Free, Plus & Enterprise Plans + Credits",
        "description": "Compare Gab AI plans. Start free with 250 credits, or upgrade to Plus for Work mode, free default-model chat, 2,000 monthly credits, and API access.",
        "keywords": "Gab AI pricing, AI subscription, Plus plan, Enterprise AI, AI credits, free AI chatbot, ChatGPT Plus alternative, AI pricing comparison, AI cost",
        "pageCopy": [
          {
            "type": "section",
            "title": "Subscription Plans",
            "copy": "Gab AI keeps pricing simple: start free for chat and creative tools, upgrade to Plus when you want Work mode for agentic multi-step jobs, or talk to us about Enterprise for your team."
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "coins",
                "color": "#10B981",
                "title": "Free",
                "subtitle": "$0",
                "description": "250 signup credits under the default verification rules, no credit card required. Available models and tools depend on account access and credit balance."
              },
              {
                "icon": "crown",
                "color": "#8B5CF6",
                "title": "Plus",
                "subtitle": "$20/month",
                "description": "or $200/year (save 17%). Work mode for agentic multi-step jobs, plus free default-model messages, 2,000 monthly credits, and API access."
              },
              {
                "icon": "gem",
                "color": "#7C3AED",
                "title": "Plus Premium",
                "subtitle": "$99/month",
                "description": "or $999/year (save 16%). Everything in Plus, with 12,000 monthly credits, 50,000 API requests/day, and priority email support. Web only."
              },
              {
                "icon": "building",
                "color": "#3B82F6",
                "title": "Enterprise",
                "subtitle": "Custom pricing",
                "description": "Everything in Plus Premium, plus volume credit discounts, custom model access, team management, dedicated support, and SLA guarantees."
              }
            ]
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "Everything Included with Plus",
            "items": [
              "Work mode — bots plan, research, use tools and files, and deliver finished results",
              "Free, unlimited default-model messages in the app",
              "2,000 credits every month for third-party models",
              "Access all AI models (GPT-5.6, Claude, Gemini, and more)",
              "Image, video, music, and 3D generation",
              "AI Memory personalization",
              "File uploads and document chat",
              "API access (10,000 requests/day)"
            ]
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "Plus Premium Adds",
            "items": [
              "Everything in Plus",
              "12,000 credits every month — 6× the Plus allotment",
              "Higher API rate limits (50,000 requests/day)",
              "Priority email support",
              "Web only — not available in the mobile apps"
            ]
          },
          {
            "type": "quick-links",
            "items": [
              {
                "title": "View Pricing Page",
                "label": "View Pricing Page",
                "description": "",
                "href": "/pricing"
              }
            ]
          },
          {
            "type": "section",
            "title": "How Credits Work",
            "copy": "Credits are used for third-party AI models (GPT, Claude, Gemini, etc.). Our default AI is always free for Plus subscribers in the app. Not included in API — API requests always use credits."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Plus includes 2,000 credits monthly",
              "Unused credits don't roll over",
              "Buy additional credit packs anytime",
              "Purchased credits never expire"
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Credit Packs",
            "copy": "Need more credits? Purchase additional packs starting at $10 for 700 credits. Larger packs offer better per-credit pricing."
          },
          {
            "type": "section",
            "title": "Credit Costs by Model",
            "copy": "Check the current model selector and estimate before sending. Credit use depends on the model, input, output, and options such as image size, batch size, or video duration. Static examples are not a quote for your next request."
          },
          {
            "type": "ordered-list",
            "items": [
              "Default-model access included with Plus applies to in-app chat; other eligible models and tools use credits.",
              "Work uses credits as it runs; media generation within a task can add costs.",
              "Image, video, music, 3D, and avatar costs depend on the selected model and options. Review the displayed estimate.",
              "Developer text requests settle from actual reliable usage in fractional credits. A temporary authorization hold is not the final charge.",
              "Voice Mode displays its credit requirements when you start a session."
            ]
          },
          {
            "type": "section",
            "title": "Frequently Asked Questions",
            "copy": ""
          },
          {
            "type": "faq",
            "items": [
              {
                "question": "What is Work mode, and is it available on Free?",
                "answer": "Work mode is Gab AI's agentic workspace for multi-step jobs. It can plan, research, work with files and tools, keep running in the cloud, and deliver finished artifacts. Work mode requires Plus or Plus Premium and uses credits while it runs."
              },
              {
                "question": "What is Plus Premium?",
                "answer": "Plus Premium is a web-only plan for businesses and power users: everything in Plus, plus 12,000 monthly credits (6× Plus), a 50,000 requests/day API limit, and priority email support. $99/month or $999/year."
              },
              {
                "question": "Are default model messages free in the API?",
                "answer": "No. Not included in API. Every API request draws from your credit balance, including Arya. Free default model messages apply to in-app chat only."
              },
              {
                "question": "Can I cancel anytime?",
                "answer": "Yes, you can cancel your subscription at any time. You'll retain access until the end of your billing period."
              },
              {
                "question": "Are there refunds?",
                "answer": "Due to computational costs, all purchases are final. See our refund policy for limited exceptions."
              },
              {
                "question": "What happens if I run out of credits?",
                "answer": "Plus subscribers keep chatting with our default AI for free in the app. Work mode and other credit-using models pause until monthly credits refresh or you buy a credit pack. Free members can purchase credits for eligible chat and creative tools or upgrade to Plus to unlock Work mode."
              },
              {
                "question": "Do credits expire?",
                "answer": "Monthly credits reset each billing cycle. Purchased credit packs never expire."
              }
            ]
          },
          {
            "type": "quick-links",
            "title": "Gab AI Business",
            "items": [
              {
                "title": "Gab AI Business",
                "href": "/docs/business",
                "description": "Company billing, Plus and Plus Premium seats, and shared credits."
              },
              {
                "title": "Existing accounts and Business",
                "href": "/docs/business-upgrade",
                "description": "Web setup for existing users, including Apple and Google subscribers."
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/34a9f287-198e-4b90-a592-77b723a93094.png"
      },
      "guideSeo": {
        "readMinutes": 6,
        "difficulty": "Beginner",
        "articleSection": "Pricing",
        "about": [
          "Gab AI pricing",
          "Plus subscription",
          "Enterprise AI",
          "AI credits"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Credits System",
          "href": "/docs/credits"
        },
        "next": {
          "label": "API Overview",
          "href": "/docs/api"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/pricing-guide"
    },
    {
      "guideType": "docs",
      "slug": "chat",
      "title": "Chat",
      "description": "The chat interface is the heart of Gab AI. Learn how to have effective conversations, manage your chat history, and unlock advanced features.",
      "badge": "Features",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "features",
      "navOrder": 15,
      "navIcon": "message",
      "searchKeywords": [
        "chat",
        "conversation",
        "message",
        "talk"
      ],
      "meta": {
        "title": "AI Chat Guide: Master Conversations, History & Replies",
        "description": "Master the Gab AI chat interface. Start conversations, manage history, edit messages, regenerate replies, and switch models mid-conversation.",
        "keywords": "AI chat, AI conversation, chat history, multi-model chat, edit AI message, regenerate AI response, Gab AI chat, ChatGPT alternative chat",
        "pageCopy": [
          {
            "type": "overview",
            "items": [
              "Writing effective prompts for better AI responses",
              "Choosing the right AI model for your task",
              "Attaching files and images to conversations",
              "Managing and organizing your chat history"
            ],
            "readTime": 4,
            "prerequisites": [
              {
                "label": "Quickstart",
                "href": "/docs/quickstart"
              },
              {
                "label": "AI Models",
                "href": "/docs/models"
              }
            ]
          },
          {
            "type": "section",
            "title": "Getting Started",
            "copy": "The chat interface is where you'll spend most of your time with Gab AI. Simply type your message in the input field at the bottom and press Enter to send. The AI will respond in the conversation thread above."
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Keyboard Shortcuts",
            "copy": "Press Enter to send a message. Use Shift+Enter to add a new line without sending. Press / to quickly access commands."
          },
          {
            "type": "section",
            "title": "Writing Effective Prompts",
            "copy": "The quality of your prompts directly affects the quality of AI responses. Here are some tips for better results:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Be specific — Include details about what you want",
              "Provide context — Share relevant background information",
              "Set the format — Specify how you want the response structured",
              "Iterate — Refine your request based on initial responses"
            ]
          },
          {
            "type": "code",
            "code": "❌ Vague: \"Write about dogs\"\n\n✅ Specific: \"Write a 200-word blog intro about the benefits \nof adopting senior dogs, targeting first-time pet owners. \nUse a warm, encouraging tone.\"",
            "language": "text",
            "title": "Example: Vague vs Specific Prompt"
          },
          {
            "type": "section",
            "title": "Choosing AI Models",
            "copy": "By default, Gab AI uses intelligent routing to select the best model for your request. You can also manually select a model using the model selector button near the input field."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Auto — Let Gab AI choose the best model automatically",
              "Default AI — Our free model for everyday in-app use",
              "GPT, Claude — Premium models for complex tasks",
              "Specialized — Image, video, and music generation models"
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Credit Usage",
            "copy": "Our default AI is free for Plus subscribers in the app. Not included in API — API requests always use credits. Other models use credits based on message length and model capability. Check the Credits page for details."
          },
          {
            "type": "section",
            "title": "Conversation Features",
            "copy": "Each conversation in Gab AI has several useful features:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Edit messages — Click on your message to edit and regenerate",
              "Regenerate — Ask the AI to try again with a different response",
              "Copy — Copy any message or code block to your clipboard",
              "Share — Share interesting conversations with others"
            ]
          },
          {
            "type": "section",
            "title": "Attachments & Files",
            "copy": "You can attach files to your messages for the AI to analyze:\n\nClick the attachment button (📎) in the input area to upload files, or simply drag and drop files directly into the chat."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Images — Upload images for analysis or as reference",
              "Documents — PDF, Word, text files for summarization or Q&A",
              "Code — Source code files for review and debugging",
              "Spreadsheets — Excel and CSV files for data analysis"
            ]
          },
          {
            "type": "section",
            "title": "Managing Conversations",
            "copy": "Keep your workspace organized by managing your conversations:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "New chat — Start a fresh conversation from the sidebar",
              "Rename — Give conversations meaningful names",
              "Archive — Hide conversations without deleting them",
              "Delete — Permanently remove conversations you no longer need",
              "Search — Find past conversations by content"
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Collections",
            "copy": "Use Collections to group related conversations together for better organization. Great for projects, topics, or workflows."
          },
          {
            "type": "quick-links",
            "title": "Related Features",
            "items": [
              {
                "icon": "brain",
                "title": "AI Memory",
                "label": "AI Memory",
                "description": "Personalize your conversations",
                "href": "/docs/memory",
                "color": "#10B981",
                "endpoint": ""
              },
              {
                "icon": "robot",
                "title": "Custom Bots",
                "label": "Custom Bots",
                "description": "Create specialized assistants",
                "href": "/docs/agents",
                "color": "#8B5CF6",
                "endpoint": ""
              },
              {
                "icon": "file",
                "title": "Files",
                "label": "Files",
                "description": "Upload and chat with documents",
                "href": "/docs/files",
                "color": "#F59E0B",
                "endpoint": ""
              },
              {
                "icon": "folder",
                "title": "Collections",
                "label": "Collections",
                "description": "Organize your conversations",
                "href": "/docs/collections",
                "color": "#3B82F6",
                "endpoint": ""
              }
            ]
          },
          {
            "type": "section",
            "title": "Chat, Auto, and Work",
            "copy": "Auto chooses a suitable way to answer. Some qualifying opening requests offer a Chat/Work choice before Work starts; choose the option you want. Work requires Plus and uses credits. Optional Work suggestions are for suitable multi-step projects, not a requirement to finish an ordinary Chat answer."
          },
          {
            "type": "section",
            "title": "Temporary chats",
            "copy": "A temporary chat stays out of ordinary history and never creates new memories. By default it does not read saved memories or collection instructions; you can opt into personalization for that chat. Saving it to history makes it follow normal memory settings."
          },
          {
            "type": "quick-links",
            "items": [
              {
                "title": "Maps & Place Guides",
                "label": "Maps & Place Guides",
                "href": "/docs/maps"
              },
              {
                "title": "Interactive answer cards",
                "label": "Interactive answer cards",
                "href": "/docs/interactive-cards"
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/9dd22ba4-3078-4016-8825-2f42cf1b7aa8.png"
      },
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Chat",
        "about": [
          "AI chat",
          "AI conversations",
          "Chat history"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Prime Bot",
          "href": "/docs/prime-agent"
        },
        "next": {
          "label": "AI Tools",
          "href": "/docs/tools"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/chat"
    },
    {
      "guideType": "docs",
      "slug": "voice",
      "title": "Voice Mode",
      "description": "Have natural, real-time voice conversations with AI. Voice Mode transforms your experience into hands-free, spoken dialogue — just like talking to a person.",
      "badge": "Features",
      "badgeColor": "#8B5CF6",
      "navSectionSlug": "features",
      "navOrder": 21,
      "navIcon": "microphone",
      "searchKeywords": [
        "voice",
        "speak",
        "talk",
        "speech",
        "audio",
        "microphone"
      ],
      "meta": {
        "title": "Voice Mode: Talk to AI in Real Time, Hands-Free",
        "description": "Voice Mode in Gab AI is a real-time, interruptible voice conversation with AI. Pick a voice, ask anything, and get natural spoken answers in milliseconds — fully hands-free.",
        "keywords": "AI voice mode, real-time voice AI, voice chatbot, conversational AI, hands-free AI, ElevenLabs voice, voice assistant, talk to AI, AI voice chat, AI speech recognition",
        "pageCopy": [
          {
            "type": "section",
            "title": "Overview",
            "copy": "Voice Mode enables natural voice conversations with your AI assistant. Simply speak your questions and hear AI responses in real-time. The conversation flows naturally — you can interrupt, ask follow-up questions, and have back-and-forth dialogue."
          },
          {
            "type": "section",
            "title": "Supported devices",
            "copy": "Voice conversations are supported on web, iOS, Android, and compatible Mac builds. Mac voice is audio-only. Camera and screen context are web features. Voice requires the relevant account access and credits; dictation into a draft is separate from voice-session billing."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "items": [
              {
                "title": "Real-time speech recognition",
                "icon": "🎙️"
              },
              {
                "title": "Natural voice responses",
                "icon": "🔊"
              },
              {
                "title": "Interrupt anytime",
                "icon": "⚡"
              },
              {
                "title": "Automatic transcript saving",
                "icon": "💾"
              }
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Microphone Required",
            "copy": "Voice Mode requires microphone access. Grant permission when prompted in your browser or app settings to start voice conversations."
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Conversation-First Feature",
            "copy": "Voice Mode is designed for spoken conversation: brainstorming, quick questions, practice, dictation, and hands-free help. For image, video, music, or other creation tasks, use chat or the dedicated AI tools."
          },
          {
            "type": "section",
            "title": "Starting a Voice Conversation",
            "copy": "Getting started with Voice Mode is simple:"
          },
          {
            "type": "steps",
            "items": [
              {
                "title": "Start Voice Mode",
                "copy": "Use the voice conversation control in the chat composer. Dictation is a separate control that inserts speech as text into your draft."
              },
              {
                "title": "Grant microphone permission",
                "copy": "Allow microphone access when your browser or operating system asks. In the Mac app, use the installed app’s microphone permission in System Settings."
              },
              {
                "title": "Talk and interrupt",
                "copy": "Wait for the connection indicator, then speak naturally. You can interrupt the response and ask a follow-up."
              },
              {
                "title": "End the call",
                "copy": "Use the End control when you are finished. Review the saved transcript in the conversation and continue by text if needed."
              }
            ]
          },
          {
            "type": "section",
            "title": "How Voice Mode Works",
            "copy": "Voice Mode uses advanced conversational AI technology to create a seamless voice experience:\n\nKey technical features:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Speech-to-Text — Your voice is transcribed instantly",
              "AI Processing — Natural language understanding generates responses",
              "Text-to-Speech — Responses are converted to natural speech",
              "Auto-Reconnection — If connection drops, it reconnects automatically",
              "Transcript Saving — All dialogue is saved to your chat history"
            ]
          },
          {
            "type": "unordered-list",
            "items": [
              "The entire process happens in real-time with minimal delay"
            ]
          },
          {
            "type": "unordered-list",
            "items": [
              "You Speak",
              "Transcribe",
              "AI Thinks",
              "AI Speaks"
            ]
          },
          {
            "type": "section",
            "title": "Understanding Visual Indicators",
            "copy": "The animated orb shows the current conversation state:\n\nA spinning indicator appears when the AI is \"thinking\" — processing your input before responding."
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Purple",
                "description": "Listening — Ready for you to speak"
              },
              {
                "icon": "",
                "title": "Green",
                "description": "Speaking — AI is responding"
              },
              {
                "icon": "",
                "title": "Amber",
                "description": "Connecting or Reconnecting"
              },
              {
                "icon": "",
                "title": "Red",
                "description": "Error — Something went wrong"
              }
            ]
          },
          {
            "type": "section",
            "title": "Controls & Keyboard Shortcuts",
            "copy": "Control your voice session with these options:\n\nOn the floating control bar:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Status Dot — Shows current state (listening, speaking, thinking)",
              "Mute Button — Toggle your microphone on/off",
              "End Button — Close the voice session"
            ]
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Space",
                "description": "Toggle mute/unmute microphone"
              },
              {
                "icon": "",
                "title": "Escape",
                "description": "End the voice session"
              },
              {
                "icon": "",
                "title": "⌘⇧V",
                "description": "Start voice mode (from chat)"
              }
            ]
          },
          {
            "type": "section",
            "title": "Voice Selection",
            "copy": "Choose from multiple AI voices to personalize your experience:"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Sarah",
                "description": "Soft, friendly",
                "color": "#EC4899"
              },
              {
                "icon": "",
                "title": "Rachel",
                "description": "Calm, professional",
                "color": "#6366F1"
              },
              {
                "icon": "",
                "title": "Adam",
                "description": "Deep, warm",
                "color": "#374151"
              },
              {
                "icon": "",
                "title": "Sam",
                "description": "Energetic, youthful",
                "color": "#F59E0B"
              },
              {
                "icon": "",
                "title": "Daniel",
                "description": "British, authoritative",
                "color": "#3B82F6"
              }
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Voice Preference",
            "copy": "Your voice preference is saved automatically. The next time you start a voice session, it will use your previously selected voice."
          },
          {
            "type": "section",
            "title": "Best Uses for Voice Mode",
            "copy": "Voice Mode excels in these scenarios:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Hands-free assistance while cooking, driving, or working out",
              "Brainstorming sessions where typing slows you down",
              "Quick questions when you're away from the keyboard",
              "Language learning and pronunciation practice",
              "Accessibility for users who prefer speech over typing",
              "Natural conversation practice and roleplaying"
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Quiet Environment",
            "copy": "For best results, use Voice Mode in a quiet environment. Background noise can affect speech recognition accuracy."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "Tips for Better Voice Interaction",
            "items": [
              "Speak clearly at a natural pace — no need to slow down",
              "Pause briefly after your question to signal you're done",
              "Interrupt the AI anytime by starting to speak",
              "Be specific with your questions for better responses",
              "Use follow-up questions to refine or expand on answers",
              "Check your microphone if the AI isn't responding"
            ]
          },
          {
            "type": "section",
            "title": "Interrupting & Barge-In",
            "copy": "Voice Mode supports natural interruption, called \"barge-in\". If the AI is speaking and you start talking, it will:\n\nThis makes conversations feel natural — you don't have to wait for the AI to finish if you want to redirect, clarify, or move on."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Stop speaking immediately",
              "Switch to listening mode",
              "Process your new input",
              "Respond to your interruption"
            ]
          },
          {
            "type": "section",
            "title": "Conversation History",
            "copy": "All voice conversations are automatically saved to your chat history:\n\nWhen you end a voice session, you're automatically taken to the chat view where you can see the full transcript and continue the conversation via text if you prefer."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Your spoken words are transcribed and saved",
              "AI responses are saved as text messages",
              "Access past voice chats like any text conversation",
              "Continue a voice conversation from any saved chat"
            ]
          },
          {
            "type": "section",
            "title": "Credit Usage",
            "copy": "Voice Mode uses a flat rate billing model based on session duration:\n\nCredits are deducted based on active conversation time. If the connection drops or you're temporarily disconnected, billing pauses until you reconnect.\n\nCredit thresholds:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Minimum to start: 30 credits (~30 seconds)",
              "Low warning: Under 180 credits remaining (~3 minutes)",
              "Critical warning: Under 35 credits remaining (~1 minute)"
            ]
          },
          {
            "type": "unordered-list",
            "items": [
              "35 credits per minute"
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Credit Monitoring",
            "copy": "Your remaining credits are displayed during the voice session. You'll receive a warning when credits are running low, and the session will end gracefully if credits are depleted."
          },
          {
            "type": "section",
            "title": "Troubleshooting",
            "copy": "Common issues and solutions:"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Microphone not working",
                "description": "Check browser permissions, ensure no other app is using the microphone, and try refreshing the page."
              },
              {
                "icon": "",
                "title": "AI not responding",
                "description": "Make sure you're unmuted, speak clearly, and check your internet connection. The status dot should be purple (listening)."
              },
              {
                "icon": "",
                "title": "Frequent disconnections",
                "description": "Auto-reconnection handles brief drops. If disconnections persist, check your network stability or try a different location."
              },
              {
                "icon": "",
                "title": "Insufficient credits",
                "description": "Voice Mode requires at least 18 credits to start. Purchase more credits or upgrade to Plus for monthly credit allocation."
              }
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Browser Support",
            "copy": "Voice Mode works best in Chrome, Edge, and Safari. Some older browsers may have limited microphone support."
          }
        ],
        "ogImage": "guides/social-cards/eaeeb99f-4d14-47b6-8d44-ce3e42f125ce.png"
      },
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Voice",
        "about": [
          "Voice AI",
          "Real-time voice chat",
          "Conversational AI"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Music Generation",
          "href": "/docs/music"
        },
        "next": {
          "label": "Custom Bots",
          "href": "/docs/agents"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/voice"
    },
    {
      "guideType": "docs",
      "slug": "agents",
      "title": "Bots",
      "description": "Create a bot with its own role and instructions, explore community bots, and save your favorites.",
      "badge": "Features",
      "badgeColor": "#8B5CF6",
      "navSectionSlug": "features",
      "navOrder": 22,
      "navIcon": "robot",
      "searchKeywords": [
        "bots",
        "bot",
        "custom",
        "assistant",
        "instructions",
        "favorites",
        "community",
        "agents"
      ],
      "meta": {
        "title": "Bots: Create Your Own AI Assistant | Gab AI",
        "description": "Create a bot with its own role and instructions, explore community bots, and save your favorites.",
        "keywords": "Gab AI bots, custom bots, AI assistants, bot instructions, community bots",
        "pageCopy": [
          {
            "type": "section",
            "title": "Meet Bots",
            "copy": "Bots are reusable AI assistants with their own roles, personalities, and instructions. Create a writing partner, a coding companion, or a tutor, then return to that bot whenever you need its help. You can also explore bots created by the community."
          },
          {
            "type": "quick-links",
            "items": [
              {
                "label": "Explore bots",
                "href": "/agents"
              },
              {
                "label": "Learn about Gab AI Work",
                "href": "/docs/work"
              }
            ]
          },
          {
            "type": "section",
            "title": "Create your first bot",
            "copy": "Sign in and open Bots from the navigation. Choose Create a bot to open the bot editor."
          },
          {
            "type": "ordered-list",
            "items": [
              "Give your bot a name and a clear purpose. Add an avatar if you want.",
              "Write instructions that explain its role, tone, and how it should respond.",
              "Review the available settings and choose the options that fit your bot.",
              "Save your bot, start a conversation, and try a realistic request.",
              "Edit the instructions as you learn what works."
            ]
          },
          {
            "type": "section",
            "title": "Give your bot useful instructions",
            "copy": "Describe the job, who the bot is helping, the style of response you want, and any rules it should follow. Include an example when a particular format matters."
          },
          {
            "type": "code",
            "language": "text",
            "title": "Example: Writing partner",
            "code": "You are my writing partner. Help me turn rough drafts into clear, conversational writing.\n\nKeep my voice and meaning. Cut filler and explain significant edits.\n\nWhen I ask for an opening, suggest three options and explain the difference.\n\nAsk about the audience and purpose if they are unclear. Never invent facts or quotations."
          },
          {
            "type": "section",
            "title": "Explore community bots",
            "copy": "Open Bots to browse community bots. Use search and categories to find an assistant for your task, open its profile, and start a conversation. Review its description to understand what it is designed to do."
          },
          {
            "type": "section",
            "title": "Keep your favorites close",
            "copy": "Save a bot as a favorite to make it easier to return to. Your saved bots appear in the sidebar. The My bots area brings together the bots you create."
          },
          {
            "type": "section",
            "title": "Manage your bots",
            "copy": "Open the menu for a bot you own to edit it or review its available management options. Check its sharing settings before making it available to others. Test changes in a new conversation with a realistic prompt."
          },
          {
            "type": "tip",
            "tipType": "note",
            "title": "Bots and Work",
            "copy": "Use a bot when you want a reusable assistant with a particular role and style. Use Work for projects that need a computer, tools, and finished artifacts such as documents, videos, or code."
          },
          {
            "type": "section",
            "title": "Availability and usage",
            "copy": "The bot controls and creation limits available to you depend on your plan. Check the current options in your account and review your credits before using paid models or tools."
          },
          {
            "type": "quick-links",
            "items": [
              {
                "label": "View plans",
                "href": "/pricing"
              },
              {
                "label": "Understand credits",
                "href": "/docs/credits"
              },
              {
                "label": "AI Memory",
                "href": "/docs/memory"
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/d30732ed-a120-4859-b9f4-9fab5e575628.png"
      },
      "guideSeo": {
        "readMinutes": 7,
        "difficulty": "Intermediate",
        "articleSection": "Bots",
        "audience": "AI builders, creators, and product teams",
        "about": [
          "Custom AI bots",
          "System prompts",
          "AI personas"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Voice Mode",
          "href": "/docs/voice"
        },
        "next": {
          "label": "AI Memory",
          "href": "/docs/memory"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/agents"
    },
    {
      "guideType": "docs",
      "slug": "memory",
      "title": "AI Memory",
      "description": "Give your AI a personalized memory that persists across conversations. The more you chat, the better it understands you.",
      "badge": "Features",
      "badgeColor": "#10B981",
      "navSectionSlug": "features",
      "navOrder": 23,
      "navIcon": "brain",
      "searchKeywords": [
        "memory",
        "remember",
        "personalize",
        "context"
      ],
      "meta": {
        "title": "AI Memory: Personalize Every Chat Across Every Model",
        "description": "Gab AI Memory remembers your preferences, projects, and style across every model and conversation. See what gets saved, how to edit memories, and how it stays private.",
        "keywords": "AI memory, persistent AI context, AI personalization, Gab AI memory, ChatGPT memory alternative, cross-model memory, custom AI context, AI remembers preferences",
        "pageCopy": [
          {
            "type": "section",
            "title": "What Is AI Memory?",
            "copy": "AI Memory allows Gab AI to remember important details about you across all your conversations. Unlike traditional chatbots that forget everything after each session, our AI builds a personalized understanding of your preferences, work, and interests."
          },
          {
            "type": "tip",
            "tipType": "note",
            "title": "Plus Feature",
            "copy": "AI Memory is available exclusively to Plus subscribers. Upgrade to unlock personalized AI experiences."
          },
          {
            "type": "section",
            "title": "How Memory Works",
            "copy": "As you chat with Gab AI, it identifies important information worth remembering:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Personal preferences — Favorite styles, formats, tones",
              "Professional context — Your role, industry, tools you use",
              "Projects — Ongoing work, goals, deadlines",
              "Communication style — How you prefer to receive information"
            ]
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "brain",
                "title": "Memory Works Everywhere",
                "description": "Memories are available in all chats, with all models and bots."
              }
            ]
          },
          {
            "type": "section",
            "title": "Managing Your Memories",
            "copy": "Open Settings → Memory to review, add, import, edit, export, or delete saved memories and review your profile overview. The existing /memory page also remains available. Memory management and using saved memories are available independently of Plus automatic learning."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "View — See all memories the AI has stored",
              "Edit — Modify or correct any memory",
              "Delete — Remove memories you don't want kept",
              "Add — Manually tell the AI to remember something"
            ]
          },
          {
            "type": "section",
            "title": "Import Memories from Another AI",
            "copy": "Already have memories stored with ChatGPT, Claude, Gemini, or another AI? You can import them into Gab AI in under a minute so your first conversation feels like your hundredth."
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "file-import",
                "title": "Switch Without Starting Over",
                "description": "Your preferences, projects, and personal context transfer seamlessly — no need to re-teach a new AI from scratch."
              }
            ]
          },
          {
            "type": "steps",
            "items": [
              {
                "title": "Copy the export prompt",
                "copy": "Open the Import Memories tool from your Memory page and copy the provided prompt. This prompt is specially written to ask your old AI to export all of its stored context about you."
              },
              {
                "title": "Paste the prompt into your old AI",
                "copy": "Open a conversation with your previous AI provider (ChatGPT, Claude, Gemini, etc.) and paste the prompt. It will output a list of everything it remembers about you."
              },
              {
                "title": "Paste the results back into Gab AI",
                "copy": "Copy the response from your old AI and paste it into the import tool. Gab AI will automatically parse each entry into individual memories that you can review, toggle on or off, and edit before saving."
              }
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Supported Providers",
            "copy": "The import prompt works with any AI that stores conversation memory or context, including ChatGPT, Claude, Gemini, Copilot, and others."
          },
          {
            "type": "section",
            "title": "Automatic Memory — No Approvals Needed",
            "copy": "When Learn from conversations is enabled for a Plus account, Gab AI can save useful facts in the background and periodically combine, update, or retire them. Explicit remember requests can save a fact with an Undo option in chat. You can inspect saved entries, their origin, and available source-conversation links in Settings → Memory. Temporary chats never create new memories."
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Learning Controls",
            "copy": "Settings → Memory has separate controls for Use memories in conversations and Learn from conversations. Turning learning off stops automatic learning; it does not delete existing memories or turn off their use. Temporary chats never create memories and read existing personalization only if you opt in. Edit or delete saved facts whenever needed."
          },
          {
            "type": "section",
            "title": "Example Memories",
            "copy": "Here are examples of things the AI might remember:"
          },
          {
            "type": "unordered-list",
            "items": [
              "User is a software engineer who works primarily with Python and React",
              "Prefers concise, bullet-point responses over long paragraphs",
              "Working on a mobile app project for a health startup",
              "Timezone is Eastern Time (ET)",
              "Has two kids named Sarah and James"
            ]
          },
          {
            "type": "section",
            "title": "Privacy & Control",
            "copy": "Your memories are:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Manage saved memories from your signed-in account.",
              "When memory use is enabled, relevant facts can be sent to the selected model provider as conversation context.",
              "Edit or delete saved memories and control automatic learning separately.",
              "Temporary chats do not create new memories; existing personalization is optional."
            ]
          },
          {
            "type": "section",
            "title": "Collection memory",
            "copy": "A collection can use account and collection memories, use only its own memories, or turn memory off. Facts learned in a collection stay scoped to it. Changing this setting does not move old entries. Temporary-chat settings still apply."
          }
        ],
        "ogImage": "guides/social-cards/6a8ac199-9d1c-4b9d-9cb0-b4cbaaca1525.png"
      },
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Personalization",
        "about": [
          "AI memory",
          "AI personalization",
          "Cross-model context"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Custom Bots",
          "href": "/docs/agents"
        },
        "next": {
          "label": "Collections",
          "href": "/docs/collections"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/memory"
    },
    {
      "guideType": "docs",
      "slug": "collections",
      "title": "Collections",
      "description": "Group related conversations and files together for better organization. Keep your work structured by project, topic, or any way you prefer.",
      "badge": "Features",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "features",
      "navOrder": 24,
      "navIcon": "folder",
      "searchKeywords": [
        "collection",
        "organize",
        "folder",
        "group"
      ],
      "meta": {
        "title": "AI Collections: Group Chats by Project, Client or Topic",
        "description": "Organize Gab AI conversations into collections by project, client, or topic. Share files, custom instructions, and context across every chat in a collection.",
        "keywords": "AI collections, organize chats, project folders, AI workspace, shared instructions, chat grouping, Gab AI collections, AI for teams",
        "pageCopy": [
          {
            "type": "section",
            "title": "What Are Collections?",
            "copy": "Collections are folders for organizing your conversations. Instead of having all chats in one long list, group them by project, topic, client, or any other organizing principle that works for you."
          },
          {
            "type": "section",
            "title": "Creating a Collection",
            "copy": "To create a new collection:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Navigate to Collections from the sidebar",
              "Click 'New Collection'",
              "Give it a name and choose an icon",
              "Optionally add a description",
              "Start adding conversations"
            ]
          },
          {
            "type": "section",
            "title": "Use Cases",
            "copy": "Here are some ways to use Collections:"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "briefcase",
                "title": "Work Projects",
                "description": "Group chats by project or client",
                "color": ""
              },
              {
                "icon": "graduation-cap",
                "title": "Learning",
                "description": "Organize study materials and research",
                "color": ""
              },
              {
                "icon": "code",
                "title": "Development",
                "description": "Keep coding discussions together",
                "color": ""
              },
              {
                "icon": "pencil",
                "title": "Writing",
                "description": "Organize creative writing projects",
                "color": ""
              },
              {
                "icon": "lightbulb",
                "title": "Ideas",
                "description": "Brainstorm and collect ideas",
                "color": ""
              }
            ]
          },
          {
            "type": "section",
            "title": "Managing Collections",
            "copy": "Keep your collections organized:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Add chats — Move existing conversations into a collection",
              "Remove chats — Take conversations out without deleting them",
              "Rename — Update collection names as projects evolve",
              "Change icon — Customize for easy visual identification",
              "Delete — Removing a collection also deletes the chats inside it, so move any chats you want to keep out of the collection first"
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Quick Add",
            "copy": "You can quickly add a chat to a collection from the chat menu (three dots) without navigating away from your conversation."
          },
          {
            "type": "section",
            "title": "Collection Instructions",
            "copy": "Each collection can have custom instructions that apply to all conversations within it. This is useful for:\n\nCollection instructions combine with your global memory and any bot instructions for highly personalized conversations."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Project context — Set background info for all related chats",
              "Response style — Define how AI should respond in this context",
              "Specialized knowledge — Add domain-specific instructions"
            ]
          },
          {
            "type": "section",
            "title": "Files in Collections",
            "copy": "Collections can also hold files:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Upload files directly to a collection",
              "Reference files across all chats in the collection",
              "Keep project documents alongside conversations"
            ]
          },
          {
            "type": "section",
            "title": "Connect a folder on your Mac",
            "copy": "In the Mac app, open the collection settings and choose a folder under Folder on this Mac. New Work chats in this collection use that folder. The selection saves immediately on this Mac; existing chats keep their own connections when you change the collection folder.\n\nManage file permissions in Settings → Mac app → Folder access. Files Work reads are sent to Gab AI’s cloud agent. Keep Gab AI open and the Mac awake and online for local file access. Set up Remote to continue these project chats from your paired phone."
          },
          {
            "type": "quick-links",
            "title": "Mac app and Remote guides",
            "items": [
              {
                "label": "Get started with the Mac app",
                "href": "/docs/mac-app"
              },
              {
                "label": "Project folders and permissions",
                "href": "/docs/project-folders"
              },
              {
                "label": "Set up Remote on iOS or Android",
                "href": "/docs/remote"
              }
            ]
          },
          {
            "type": "section",
            "title": "Sidebar order and longer lists",
            "copy": "The sidebar initially shows up to ten collections. Use See more or See less for longer lists. Choose a sort order or save a custom order; a folder’s contents can have their own sorting preference. Changing the display order does not move or delete conversations or files."
          },
          {
            "type": "section",
            "title": "Memory in a collection",
            "copy": "Choose account and collection memory, collection-only memory, or memory off for a collection. New facts learned in a collection remain in that collection. The setting controls context; it does not grant access to a connected Mac folder."
          }
        ],
        "ogImage": "guides/social-cards/23d9e4d9-5418-4412-ae5b-b202505874dd.png"
      },
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Productivity",
        "about": [
          "AI collections",
          "Project organization",
          "AI workspaces"
        ]
      },
      "navigation": {
        "previous": {
          "label": "AI Memory",
          "href": "/docs/memory"
        },
        "next": {
          "label": "Bookmarks",
          "href": "/docs/bookmarks"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/collections",
      "updatedAt": "2026-09-15T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "tasks",
      "title": "Automated Tasks",
      "description": "Put AI to work for you. Set up scheduled tasks that run automatically and deliver personalized content—daily briefings, devotionals, research updates, and more.",
      "badge": "Features",
      "badgeColor": "#22C55E",
      "navSectionSlug": "features",
      "navOrder": 29,
      "navIcon": "clock",
      "searchKeywords": [
        "task",
        "automate",
        "schedule",
        "recurring",
        "cron"
      ],
      "meta": {
        "title": "Automated AI Tasks: Schedule Daily Briefings, Research & Reminders",
        "description": "Schedule recurring AI tasks in Gab AI. Get daily news briefings, market research, devotionals, weather reports, and reminders delivered automatically — set once, run forever.",
        "keywords": "AI automated tasks, scheduled AI, daily AI briefing, AI cron jobs, AI automation, AI bot schedule, daily devotional AI, recurring AI task, AI assistant scheduling",
        "pageCopy": [
          {
            "type": "section",
            "title": "What Are Tasks?",
            "copy": "Tasks are scheduled AI assistants that proactively work for you. Instead of manually asking AI the same questions every day, you can set up automated tasks that run on your schedule and deliver results directly to you."
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "bolt",
                "title": "Set It and Forget It",
                "description": "Tasks work while you sleep—delivering insights without lifting a finger."
              }
            ]
          },
          {
            "type": "tip",
            "tipType": "note",
            "title": "Plus Feature",
            "copy": "Automated Tasks are available exclusively to Plus subscribers. Upgrade to unlock scheduled AI assistance."
          },
          {
            "type": "section",
            "title": "Task Templates",
            "copy": "Choose from curated templates for common use cases, or create your own custom task:"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Daily Bible Verse",
                "description": "Scripture with reflection, customizable translation and themes",
                "color": ""
              },
              {
                "icon": "",
                "title": "Daily Devotional",
                "description": "Morning spiritual guidance with journaling prompts",
                "color": ""
              },
              {
                "icon": "",
                "title": "Prayer Prompts",
                "description": "Daily prayer topics based on your life and community",
                "color": ""
              }
            ],
            "title": "Faith & Devotional",
            "icon": "book-bible"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Daily News Briefing",
                "description": "Top stories on topics you choose",
                "color": ""
              },
              {
                "icon": "",
                "title": "Industry Digest",
                "description": "Professional updates in your field",
                "color": ""
              },
              {
                "icon": "",
                "title": "Local News Summary",
                "description": "What's happening in your area",
                "color": ""
              }
            ],
            "title": "News & Information",
            "icon": "newspaper"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Stock Market Summary",
                "description": "Daily or weekly market analysis",
                "color": ""
              },
              {
                "icon": "",
                "title": "Portfolio Updates",
                "description": "Your investments tracked",
                "color": ""
              },
              {
                "icon": "",
                "title": "Economic Briefing",
                "description": "Financial news that matters",
                "color": ""
              }
            ],
            "title": "Finance & Markets",
            "icon": "chart-line"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Research Digest",
                "description": "Academic and industry developments",
                "color": ""
              },
              {
                "icon": "",
                "title": "Learning Summary",
                "description": "Progress tracking and study materials",
                "color": ""
              },
              {
                "icon": "",
                "title": "Topic Deep Dive",
                "description": "Ongoing exploration of interests",
                "color": ""
              }
            ],
            "title": "Research & Learning",
            "icon": "graduation-cap"
          },
          {
            "type": "section",
            "title": "How Tasks Work",
            "copy": "Setting up a task takes just a few steps:"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "Choose a Template",
                "description": "Pick from curated templates for news, devotionals, market updates, and more—or create a completely custom task with your own instructions."
              },
              {
                "icon": "",
                "title": "Configure Preferences",
                "description": "Personalize the output—select topics, themes, detail level, and any specific instructions to make the task uniquely yours."
              },
              {
                "icon": "",
                "title": "Set Your Schedule",
                "description": "Choose when and how often your task runs—once, daily, weekly, or monthly at the time that works best for you."
              },
              {
                "icon": "",
                "title": "Get Results Delivered",
                "description": "Receive results in your email inbox or chat conversations—without lifting a finger."
              }
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Powered by Arya",
            "copy": "All tasks automatically use Arya, our exclusive AI model built with Christian values. No model selection needed—just configure your preferences and schedule."
          },
          {
            "type": "section",
            "title": "Scheduling Options",
            "copy": "Flexible scheduling to match your routine:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Once — Run one time at a specific date and time",
              "Daily — Every day at your chosen time",
              "Weekly — On a specific day each week",
              "Monthly — On a specific day each month"
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Timezone Aware",
            "copy": "All schedules run in your local timezone. Tasks execute reliably whether you're an early riser or night owl."
          },
          {
            "type": "section",
            "title": "Email Delivery",
            "copy": "Get task results delivered straight to your inbox:\n\nResults also appear in your chat conversations for easy reference and follow-up questions."
          },
          {
            "type": "ordered-list",
            "items": [
              "Morning (7 AM) — Start your day with fresh insights",
              "Afternoon (12 PM) — Midday catch-up",
              "Evening (6 PM) — End-of-day summary",
              "Immediately — As soon as the task completes"
            ]
          },
          {
            "type": "section",
            "title": "Managing Your Tasks",
            "copy": "You have full control over all your tasks:\n\nNavigate to the Tasks section from the sidebar to manage all your scheduled tasks."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "View — See all active tasks with status and statistics",
              "Edit — Update configuration, schedule, or delivery preferences",
              "Pause — Temporarily stop a task without deleting it",
              "Delete — Remove a task to stop all future runs",
              "View Latest Results — Jump to the most recent conversation"
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Finding Past Results",
            "copy": "Each time a task runs, it creates a new conversation with a dated title (e.g., \"Daily News Briefing - Jan 26, 2026\"). You can find all past results in your chat history."
          },
          {
            "type": "section",
            "title": "Task Statistics",
            "copy": "Track performance for each task:"
          },
          {
            "type": "ordered-list",
            "items": [
              "Total Runs — How many times the task has executed",
              "Successful Runs — Completions without errors",
              "Failed Runs — Any issues encountered",
              "Last Run — When it last ran and the result",
              "Next Run — When the task will run next"
            ]
          },
          {
            "type": "section",
            "title": "Example Use Cases",
            "copy": ""
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "sun",
                "title": "Morning Routine",
                "description": "Daily devotional at 6 AM, news briefing at 7 AM",
                "color": "#F59E0B"
              },
              {
                "icon": "briefcase",
                "title": "Professional",
                "description": "Weekly industry digest every Monday morning",
                "color": "#3B82F6"
              },
              {
                "icon": "chart-line",
                "title": "Investor",
                "description": "Market summary every weekday at market close",
                "color": "#22C55E"
              },
              {
                "icon": "book",
                "title": "Researcher",
                "description": "Monthly research compilation on topics of interest",
                "color": "#9333EA"
              }
            ]
          },
          {
            "type": "section",
            "title": "Credits & Usage",
            "copy": "Tasks consume credits when they run. Consider these tips:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Use brief/summary modes for daily tasks to conserve credits",
              "Run comprehensive analysis tasks less frequently (weekly/monthly)",
              "Monitor your credit usage in Settings → Credits",
              "Pause tasks temporarily if you need to conserve credits"
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Credit Estimates",
            "copy": "Each task shows an estimated credit cost before you create it. Actual usage may vary based on output length and complexity."
          },
          {
            "type": "section",
            "title": "Choose Run using: Chat or Work",
            "copy": "New standalone tasks and template tasks default to Chat. A schedule created inside an existing Work conversation defaults to Work. Chat runs use the selected text model and create a fresh result conversation. Work runs use the Work workspace and its credits and access requirements. Existing schedules keep their saved mode; check Run using before your next run."
          },
          {
            "type": "section",
            "title": "Delete a result or change the mode",
            "copy": "Deleting a result conversation does not delete the schedule. If a schedule is explicitly bound to a Work chat and that chat is deleted, the schedule is retained but paused. Changing a Work-bound schedule to Chat clears its Work binding. Use Tasks to edit, pause, resume, or delete the schedule itself."
          }
        ],
        "ogImage": "guides/social-cards/bd47e056-203a-475d-84d4-ece92db3bec5.png"
      },
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Intermediate",
        "articleSection": "Automation",
        "about": [
          "Automated AI tasks",
          "Scheduled AI",
          "AI automation"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Web Search",
          "href": "/docs/web-search"
        },
        "next": {
          "label": "Import Chats",
          "href": "/docs/import-chats"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/tasks"
    },
    {
      "guideType": "docs",
      "slug": "data-export",
      "title": "Download Your Data",
      "description": "Take a portable ZIP archive of everything tied to your Gab AI account — anytime, in one click. Available in Settings and through the developer API.",
      "badge": "Features",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "features",
      "navOrder": 31,
      "navIcon": "box-archive",
      "searchKeywords": [
        "data export",
        "download data",
        "account export",
        "archive",
        "gdpr",
        "ccpa",
        "portability",
        "backup"
      ],
      "meta": {
        "title": "Download Your Gab AI Data: Full Account Export (ZIP or API)",
        "description": "Export your full Gab AI account — chats, memories, files, custom bots, collections, voice sessions, bookmarks, and purchases — as a portable ZIP or JSON. One click in Settings or one call via the API.",
        "keywords": "Gab AI data export, download my data, account export, GDPR export, CCPA export, data portability, AI chat archive, account backup, JSON export, ZIP export, AI conversation backup",
        "pageCopy": [
          {
            "type": "section",
            "title": "Overview",
            "copy": "Your data belongs to you. Gab AI ships a one-click data export that packages every record we hold for your account into a single, portable ZIP archive you can save, search, or migrate. Inside you'll find one JSON file per data type (conversations, memories, files, etc.) plus a markdown file per conversation that's easy to open in any text editor. The same data is available programmatically over the developer API for backups, dashboards, or compliance tooling.\n\nThe export includes:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Profile, preferences, and account flags (Plus status, email verification, etc.)",
              "Every conversation you've had — titles, timestamps, and full message bodies",
              "All AI memories saved on your behalf (auto-generated and manually added)",
              "Files you've uploaded, with their metadata and CDN URLs",
              "Custom bots you've built",
              "Collections (chat + file groupings) and their custom instructions",
              "Voice mode session metadata",
              "Bookmark folders and structure",
              "Automated tasks you've scheduled",
              "Plus subscription purchases and one-time credit-pack purchases",
              "Referrals you've sent or received",
              "Feedback you've submitted to us",
              "API key metadata (names, last-used, usage counts — never the secret values)"
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "What's NOT included",
            "copy": "Deleted conversations and chats marked as temporary (incognito mode) are filtered out before we build the archive — if you can't see it in the app, it isn't in the export. We also strip passwords, password reset tokens, MFA secrets, backup codes, payment-processor identifiers (Authorize.net / Valmar customer + payment-method IDs), memory embedding vectors, files attached to temporary chats, and the secret value of API keys."
          },
          {
            "type": "section",
            "title": "What's in the ZIP",
            "copy": "The archive is organized so you can drag the file into any unzip tool and immediately understand what's where:"
          },
          {
            "type": "code",
            "code": "gab-ai-account-data-2026-05-02.zip\n├── README.md                  # human-readable overview, counts, layout\n├── manifest.json              # exported_at, counts, truncated, schema_version\n├── profile.json               # your account profile\n├── conversations.json         # raw bundle of every conversation\n├── conversations/             # one .md file per conversation, easy to read\n│   ├── 2026-05-01-untitled-conversation-abc123.md\n│   └── ...\n├── memories.json              # raw memory bundle\n├── memories.txt               # one memory per line, easy to grep\n├── files.json\n├── agents.json\n├── collections.json\n├── voice_sessions.json\n├── bookmark_folders.json\n├── inference_tasks.json\n├── purchases.json\n├── credit_purchases.json\n├── referrals.json\n├── feedback.json\n└── api_keys.json              # metadata only — secret values are never re-exported",
            "language": "text",
            "title": "Archive contents"
          },
          {
            "type": "section",
            "title": "From the App",
            "copy": "Most users will grab their data through the Settings UI. The download triggers a ZIP file save on web and the OS share sheet on iOS / Android."
          },
          {
            "type": "steps",
            "items": [
              {
                "title": "Open Settings",
                "copy": "Click your avatar in the top-right corner of Gab AI and pick Settings. On mobile, tap the menu icon and select Settings."
              },
              {
                "title": "Go to General",
                "copy": "In the Settings sidebar, select General. Your language preferences live here, along with the data controls."
              },
              {
                "title": "Find &quot;Download your data&quot;",
                "copy": "Scroll past the language and verification rows to the Download your data section. It sits right above the Danger Zone."
              },
              {
                "title": "Click Download ZIP",
                "copy": "Click Download ZIP. Gab AI prepares an archive named gab-ai-account-data-YYYY-MM-DD.zip and either saves it to your downloads folder (web) or opens the share sheet (mobile). Heavy accounts may take a few seconds — that's normal."
              },
              {
                "title": "Open the archive",
                "copy": "Unzip the file with whatever tool your OS provides. Start with the README.md for a tour, browse conversations/ to read individual chats, or load the per-collection JSON files into the tool of your choice."
              }
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Throttling",
            "copy": "Account exports are capped at 5 per hour to protect server capacity. That's plenty for legitimate \"I want a backup\" or \"I'm migrating tools\" workflows."
          },
          {
            "type": "section",
            "title": "From the Developer API",
            "copy": "Plus subscribers with API access can fetch the same data over HTTP from GET /v1/account/export. The developer API defaults to JSON for easy programmatic consumption and accepts ?format=zip when you want the same archive the Settings UI downloads.\n\nPass ?include_messages=false if you only want metadata for a sanity check — that drops the (often very large) conversation messages array and shrinks the bundle dramatically."
          },
          {
            "type": "code",
            "code": "# JSON (default) — easy to script against\ncurl https://gab.ai/v1/account/export \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\\\n  -o gab-ai-account-data.json\n\n# ZIP archive — matches the in-app download\ncurl \"https://gab.ai/v1/account/export?format=zip\" \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\\\n  -o gab-ai-account-data.zip",
            "language": "bash",
            "title": "Quick start"
          },
          {
            "type": "code",
            "code": "const fs = require('fs');\n\nconst response = await fetch('https://gab.ai/v1/account/export', {\n  headers: { Authorization: 'Bearer YOUR_API_KEY' },\n});\n\nif (!response.ok) {\n  throw new Error(\\`Export failed: \\${response.status}\\`);\n}\n\nconst bundle = await response.json();\nfs.writeFileSync(\n  'gab-ai-account-data.json',\n  JSON.stringify(bundle, null, 2),\n);\n\nconsole.log('Exported', bundle.counts);",
            "language": "javascript",
            "title": "Node.js"
          },
          {
            "type": "code",
            "code": "import json\nimport requests\n\nresp = requests.get(\n    \"https://gab.ai/v1/account/export\",\n    headers={\"Authorization\": \"Bearer YOUR_API_KEY\"},\n    timeout=120,\n)\nresp.raise_for_status()\n\nwith open(\"gab-ai-account-data.json\", \"w\") as f:\n    json.dump(resp.json(), f, indent=2)\n\nprint(\"Exported\", resp.json()[\"counts\"])",
            "language": "python",
            "title": "Python"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Same data, two views",
            "copy": "The developer API and the in-app Settings button hit the same backend — you get the same data either way. JSON is the canonical shape; the ZIP is just a friendlier wrapping that adds a README and per-conversation markdown files."
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "Common Use Cases",
            "items": [
              "Personal backups — keep an offline copy of your chats and memories",
              "Migrating to / from another tool",
              "Compliance — fulfilling GDPR / CCPA right-to-access requests for yourself",
              "Custom analytics — feed your conversation history into your own dashboards",
              "Audit trails — track which bots, files, and collections you've used over time",
              "Pre-deletion archive — grab a copy before deleting your account"
            ]
          },
          {
            "type": "section",
            "title": "FAQ",
            "copy": "Are deleted or temporary chats included?\n\nNo. We exclude every conversation that's been deleted (soft or hard) and every chat that was started in temporary / incognito mode. Same goes for memories you rejected, soft-deleted custom bots and collections, and files attached to temporary chats. If it isn't visible in the app, it isn't in the export.\n\nDoes the export contain my password or payment info?\n\nNo. We strip all credentials, password reset tokens, MFA secrets, backup codes, and payment-processor IDs before returning the archive.\n\nWhat if my account is too large to fit?\n\nEach collection is capped at a generous limit (5,000 conversations, 10,000 files, etc.). If a cap is hit, the JSON response includes a truncated object listing the affected collections, and the ZIP's README spells it out. Email support@gab.ai for a complete offline archive.\n\nAre the API keys in the export usable?\n\nNo — only metadata is included (name, creation time, last-used timestamp, usage counts). Secret values are never re-exported, since we only display them once at creation.\n\nDoes this cost credits?\n\nNo. Account exports are free and don't consume API credits. They count against your daily request limit but at a tiny rate compared to inference calls."
          }
        ],
        "ogImage": "guides/social-cards/f9a2789c-7a75-43de-9ff3-0f3f8f721d0a.png"
      },
      "guideSeo": {
        "readMinutes": 6,
        "difficulty": "Beginner",
        "articleSection": "Privacy",
        "audience": "All Gab AI users",
        "about": [
          "Data export",
          "Account backup",
          "GDPR data portability"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Import Chats",
          "href": "/docs/import-chats"
        },
        "next": {
          "label": "Privacy & Values",
          "href": "/docs/privacy"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/data-export"
    },
    {
      "guideType": "docs",
      "slug": "api-endpoints",
      "title": "API Endpoints",
      "description": "Complete reference for all Gab AI API endpoints: chat completions (with vision / image input), file uploads, image generation, image editing with reference images, video generation, text-to-speech, embeddings, and more.",
      "badge": "Developer",
      "badgeColor": "#8B5CF6",
      "navSectionSlug": "developer",
      "navOrder": 8,
      "navIcon": "server",
      "searchKeywords": [
        "endpoint",
        "rest",
        "http",
        "request",
        "embedding",
        "embeddings",
        "files",
        "file upload",
        "image upload",
        "vision",
        "multimodal",
        "image_url",
        "file_id",
        "image edit",
        "image editing",
        "reference image",
        "flux-2-pro",
        "images/edit",
        "images/edits",
        "delete image",
        "delete generated image",
        "files library",
        "DELETE /v1/images"
      ],
      "meta": {
        "title": "Gab AI API Reference: Chat, Vision, Images, Video, Audio & Embeddings",
        "description": "Complete REST API reference for Gab AI. Chat completions, vision and image input, file uploads, image generation, image editing with reference images, video generation, text-to-speech, and embeddings — all OpenAI-compatible.",
        "keywords": "Gab AI API reference, REST API, chat completions API, vision API, image generation API, image editing API, reference image API, flux-2-pro, video generation API, text-to-speech API, embeddings API, file upload API, multimodal AI API, OpenAI-compatible API, image_url, file_id",
        "pageCopy": [
          {
            "type": "section",
            "title": "Base URL",
            "copy": "Use https://gab.ai/v1 as the base_url / baseURL in OpenAI-compatible SDKs. Individual requests append an endpoint such as /chat/completions. GET https://gab.ai/v1/models lists current model IDs and aliases (authentication optional)."
          },
          {
            "type": "unordered-list",
            "items": [
              "https://gab.ai/v1"
            ]
          },
          {
            "type": "section",
            "title": "Chat Completions",
            "copy": "Generate a response from an AI model based on a conversation history. This is the primary endpoint for text generation and follows the OpenAI chat completions format.\n\nYou can pass a tools array and optional tool_choice in the request body (see the table above). Supported models can then request one or more tool calls; you run the tools and send the results back in follow-up requests. Tools are only used when you send tools and the model supports function calling (see model capabilities below).\n\nAssistant messages in messages may include tool_calls: an array of objects with id, type: \"function\", and function: { name, arguments }. Tool result messages use role: \"tool\" and must include tool_call_id (matching the id of the corresponding tool call) and content (the tool’s result string).\n\nchoices[0].message may include content (string or null) and/or tool_calls (array with the same shape: id, type, function.name, function.arguments). choices[0].finish_reason may be \"tool_calls\" when the model is requesting tool use (in addition to \"stop\", \"length\", etc.).\n\nStream chunks may include delta.tool_calls (array of tool call deltas). The final chunk may have finish_reason: \"tool_calls\" when the model is requesting tool use.\n\nSend messages plus optional tools and tool_choice. If the response has finish_reason: \"tool_calls\" and message.tool_calls, run each tool, append the assistant message (including its tool_calls) to messages, append one message per result with role \"tool\", tool_call_id, and content, then call the API again with the updated messages. Repeat until finish_reason is \"stop\" or another non–tool-call reason.\n\nVision-capable models accept images in the messages array using OpenAI's multimodal content format: a user message's content can be an array of parts, where each part is either {'{ type: \"text\", text }'} or {'{ type: \"image_url\", image_url: { url } }'}. The image URL may be a public HTTPS URL, a base64 data URL, or a { file_id } returned by /v1/files.\n\nUse /v1/models to discover vision-capable models — look for capabilities.image_input: true. Passing images to a model that does not support image input returns a 400 unsupported_image_input error.\n\nPass response_format to constrain output to valid JSON, either freely (json_object) or against a strict JSON Schema (json_schema). The shape matches OpenAI's structured-outputs API exactly.\n\nPOST /chat/completions"
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body",
              "Tool and function calling",
              "Request messages",
              "Response (non-streaming)",
              "Response (streaming)",
              "Multi-turn tool flow",
              "Image input (vision)",
              "Structured outputs (JSON mode)"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "model (string, required)",
                "answer": "Model ID or alias (e.g. \"arya\", \"gpt-5-5\", \"gpt-5.5\", \"claude-sonnet-4-5\", \"anthropic/claude-sonnet-4-5\"), or \"auto\" / \"gab/auto\" to classify the kickoff. Display names like \"Claude Opus 4.8\" will not match. GET /v1/models lists ids and aliases. Responses include X-Gab-Routed-Model, X-Gab-Router-Intent, and X-Gab-Router-Coding-Tier when model is auto. The slug Auto maps to each coding tier is configurable and can change — read those headers rather than assuming a particular engine."
              },
              {
                "question": "messages (array, required)",
                "answer": "Array of message objects with role and content. Assistant messages may include tool_calls; tool result messages use role \"tool\" with tool_call_id and content."
              },
              {
                "question": "temperature (number, optional)",
                "answer": "Sampling temperature (0-2, default 1)"
              },
              {
                "question": "max_tokens (integer, optional)",
                "answer": "Maximum tokens to generate"
              },
              {
                "question": "stream (boolean, optional)",
                "answer": "Enable streaming responses (default false)"
              },
              {
                "question": "top_p (number, optional)",
                "answer": "Nucleus sampling parameter (0-1)"
              },
              {
                "question": "tools (array, optional)",
                "answer": "List of tools the model may call. Each item has type: \"function\" and a function object with name, description, and parameters (JSON Schema). Same structure as OpenAI's tools parameter."
              },
              {
                "question": "tool_choice (string | object, optional)",
                "answer": "How the model should use tools: \"auto\" (let the model decide), \"none\" (disable tools), \"required\", or { type: \"function\", function: { name: \"<tool_name>\" } } to force a specific tool."
              },
              {
                "question": "parallel_tool_calls (boolean, optional)",
                "answer": "Forwarded to OpenAI-compatible providers that support parallel tool calls."
              },
              {
                "question": "reasoning_effort (string, optional)",
                "answer": "For reasoning models: \"minimal\", \"low\", \"medium\", or \"high\". Also accepts reasoning: { effort }. On Anthropic models this is mapped to thinking.budget_tokens."
              },
              {
                "question": "response_format (object, optional)",
                "answer": "Structured output mode. Supports OpenAI's shape: { type: \"text\" } (default), { type: \"json_object\" }, or { type: \"json_schema\", json_schema: { name, strict?, schema } } where schema is a JSON Schema. Best results on models with capabilities.function_calling: true."
              },
              {
                "question": "web_search (boolean, optional)",
                "answer": "Set to true to let the platform perform a live web search for the request and supply the results to the model (with citations). Only takes effect on models that advertise capabilities.web_search: true (for example \"arya\") — it is ignored for other models. You can also trigger a search without this flag by asking in plain language (e.g. \"search the web for ...\"); for ordinary questions that merely need fresh information, pass web_search: true explicitly."
              },
              {
                "question": "web_search_options (object, optional)",
                "answer": "OpenAI-compatible alternative to web_search. Providing this object (even an empty {}) enables web search on web-search-capable models, the same as web_search: true."
              }
            ]
          },
          {
            "type": "code",
            "code": "const response = await fetch('https://gab.ai/v1/chat/completions', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'Bearer YOUR_API_KEY'\n  },\n  body: JSON.stringify({\n    model: 'arya',\n    messages: [\n      { role: 'system', content: 'You are a helpful assistant.' },\n      { role: 'user', content: 'What is the capital of France?' }\n    ],\n    temperature: 0.7,\n    max_tokens: 1000\n  })\n});",
            "language": "javascript",
            "title": "Request Example"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"chatcmpl-abc123\",\n  \"object\": \"chat.completion\",\n  \"created\": 1704067200,\n  \"model\": \"arya\",\n  \"choices\": [\n    {\n      \"index\": 0,\n      \"message\": {\n        \"role\": \"assistant\",\n        \"content\": \"The capital of France is Paris.\"\n      },\n      \"finish_reason\": \"stop\"\n    }\n  ],\n  \"usage\": {\n    \"prompt_tokens\": 25,\n    \"completion_tokens\": 12,\n    \"total_tokens\": 37,\n    \"credits_used\": 1\n  }\n}",
            "language": "json",
            "title": "Response Example"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"web_search\": true,\n  \"messages\": [\n    { \"role\": \"user\", \"content\": \"Where can I watch the new film Citizen Vigilante in Australia?\" }\n  ]\n}",
            "language": "json",
            "title": "Request with web search enabled"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Web search",
            "copy": "Web search runs only on models with capabilities.web_search: true (such as arya) and only when you opt in — either with web_search: true (or web_search_options) or by asking in plain language like \"search the web for ...\". The platform performs the search, injects the results into the model's context, and the model cites its sources. For ordinary questions that just need fresh information, set web_search: true explicitly."
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"messages\": [\n    { \"role\": \"user\", \"content\": \"What is the weather in Paris?\" }\n  ],\n  \"tools\": [\n    {\n      \"type\": \"function\",\n      \"function\": {\n        \"name\": \"get_weather\",\n        \"description\": \"Get the current weather for a location.\",\n        \"parameters\": {\n          \"type\": \"object\",\n          \"properties\": {\n            \"location\": { \"type\": \"string\", \"description\": \"City name\" }\n          },\n          \"required\": [\"location\"]\n        }\n      }\n    }\n  ],\n  \"tool_choice\": \"auto\"\n}",
            "language": "json",
            "title": "Request with tools (non-streaming)"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"chatcmpl-xyz\",\n  \"object\": \"chat.completion\",\n  \"choices\": [\n    {\n      \"index\": 0,\n      \"message\": {\n        \"role\": \"assistant\",\n        \"content\": null,\n        \"tool_calls\": [\n          {\n            \"id\": \"call_abc123\",\n            \"type\": \"function\",\n            \"function\": {\n              \"name\": \"get_weather\",\n              \"arguments\": \"{\\\\\"location\\\\\": \\\\\"Paris\\\\\"}\"\n            }\n          }\n        ]\n      },\n      \"finish_reason\": \"tool_calls\"\n    }\n  ],\n  \"usage\": { \"prompt_tokens\": 20, \"completion_tokens\": 25, \"total_tokens\": 45, \"credits_used\": 1 }\n}",
            "language": "json",
            "title": "Response with tool_calls"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"messages\": [\n    { \"role\": \"user\", \"content\": \"What is the weather in Paris?\" },\n    {\n      \"role\": \"assistant\",\n      \"content\": null,\n      \"tool_calls\": [\n        {\n          \"id\": \"call_abc123\",\n          \"type\": \"function\",\n          \"function\": { \"name\": \"get_weather\", \"arguments\": \"{\\\\\"location\\\\\": \\\\\"Paris\\\\\"}\" }\n        }\n      ]\n    },\n    {\n      \"role\": \"tool\",\n      \"tool_call_id\": \"call_abc123\",\n      \"content\": \"Sunny, 22°C\"\n    }\n  ],\n  \"tools\": [\n    { \"type\": \"function\", \"function\": { \"name\": \"get_weather\", \"description\": \"Get weather for a location.\", \"parameters\": { \"type\": \"object\", \"properties\": { \"location\": { \"type\": \"string\" } }, \"required\": [\"location\"] } } }\n  ],\n  \"tool_choice\": \"auto\"\n}",
            "language": "json",
            "title": "Follow-up request with tool results"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"messages\": [\n    {\n      \"role\": \"user\",\n      \"content\": [\n        { \"type\": \"text\", \"text\": \"What's in this image?\" },\n        { \"type\": \"image_url\", \"image_url\": { \"url\": \"https://example.com/photo.jpg\" } }\n      ]\n    }\n  ]\n}",
            "language": "json",
            "title": "Image via public URL"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"messages\": [\n    {\n      \"role\": \"user\",\n      \"content\": [\n        { \"type\": \"text\", \"text\": \"Describe this receipt.\" },\n        { \"type\": \"image_url\", \"image_url\": { \"url\": \"data:image/png;base64,iVBORw0KGgoAAAA...\" } }\n      ]\n    }\n  ]\n}",
            "language": "json",
            "title": "Image via base64 data URL"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"messages\": [\n    {\n      \"role\": \"user\",\n      \"content\": [\n        { \"type\": \"text\", \"text\": \"Summarize this diagram.\" },\n        { \"type\": \"image_url\", \"image_url\": { \"file_id\": \"<FILE_ID_FROM_V1_FILES>\" } }\n      ]\n    }\n  ]\n}",
            "language": "json",
            "title": "Image via /v1/files upload (recommended for large / reused images)"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"messages\": [\n    { \"role\": \"system\", \"content\": \"Extract the user's name and age. Reply with JSON only.\" },\n    { \"role\": \"user\", \"content\": \"Hi, I'm Alice and I'm 30.\" }\n  ],\n  \"response_format\": { \"type\": \"json_object\" }\n}",
            "language": "json",
            "title": "json_object — free-form JSON"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"messages\": [\n    { \"role\": \"user\", \"content\": \"Extract the user's name and age from: 'Hi, I'm Alice and I'm 30.'\" }\n  ],\n  \"response_format\": {\n    \"type\": \"json_schema\",\n    \"json_schema\": {\n      \"name\": \"person\",\n      \"strict\": true,\n      \"schema\": {\n        \"type\": \"object\",\n        \"properties\": {\n          \"name\": { \"type\": \"string\" },\n          \"age\":  { \"type\": \"integer\" }\n        },\n        \"required\": [\"name\", \"age\"],\n        \"additionalProperties\": false\n      }\n    }\n  }\n}",
            "language": "json",
            "title": "json_schema — strict, typed output"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Provider-agnostic",
            "copy": "The OpenAI tool-call shape works identically across every supported provider — arya, gpt-*, claude-*, gemini-*, deepseek-*, kimi-*, qwen-*, etc. We translate to each provider's native format (e.g., Anthropic's tool_use/ tool_result blocks) on the way out and back. You always send and receive OpenAI-style tool_calls / role: \"tool\" messages, so existing OpenAI SDKs and agent frameworks (LangChain, Vercel AI SDK, OpenCode, LlamaIndex, …) work unchanged."
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Token Usage",
            "copy": "All responses include a usage object with token counts and the final credits_used. Developer API text requests settle at provider cost plus a margin floor — not the published per-request tariff. Authorization holds estimate input and requested output cost; the listed tariff remains a fallback when token usage is missing or unreliable. Long outputs or extended thinking can therefore use additional credits beyond the hold. When max_tokens is omitted, the hold assumes 16,384 output tokens rather than the model's full ceiling. If the authorization exceeds your balance, the API returns insufficient_credit_authorization — reduce max_tokens, choose a less expensive model, or add credits. If actual usage settles above the remaining balance, the request fails with insufficient_credits and is not billed. Arya is not free on the API and follows the same rule as every other model — provider token cost plus the margin floor, settled to six decimal places without a one-credit minimum when usage is reliable. The free default model applies to in-app chat only. When the provider reports a prompt-cache hit, usage also includes prompt_tokens_details.cached_tokens (OpenAI) or cache_read_input_tokens (Anthropic). Cache savings reduce the charge when possible, but never below the API provider-cost floor."
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Which image input should I use?",
            "copy": "Use a public URL when the image is already hosted; use a base64 data URL for small, one-off images; and use /v1/files + file_id for larger images or anything you'll reference across multiple requests (avoids re-uploading and keeps request bodies small)."
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Compatibility",
            "copy": "Structured outputs work best on models that advertise capabilities.function_calling: true. A few providers don't support strict json_schema — if the upstream rejects the schema you'll receive a 400 error rather than a silent fallback."
          },
          {
            "type": "section",
            "title": "OpenAI Responses",
            "copy": "Gab AI supports OpenAI's newer Responses API shape for Codex-style agent clients and SDKs that use input / output items instead of chat messages. The endpoint maps Responses API function calls onto the same tool-calling pipeline as chat completions.\n\nStreaming responses emit Responses-style events such as response.created, response.output_text.delta, response.function_call_arguments.delta, response.function_call_arguments.done, and response.completed.\n\nPass previous_response_id to continue a stored turn without resending the full transcript. Retrieve a stored result with GET /v1/responses/{id} (24-hour TTL).\n\nPOST /responses\n\nGET /responses/{id}"
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "model (string, required)",
                "answer": "Model ID such as \"arya\", \"gpt-5-5\", or \"claude-sonnet-4-5\", or \"auto\" / \"gab/auto\". Auto classifies the kickoff and sticks the model across previous_response_id turns. Inspect X-Gab-Routed-Model and X-Gab-Router-Coding-Tier."
              },
              {
                "question": "input (string | array, optional)",
                "answer": "A plain prompt string or Responses API input items: message, function_call, and function_call_output. Required unless previous_response_id is set."
              },
              {
                "question": "previous_response_id (string, optional)",
                "answer": "Continue from a stored Responses result instead of resending the full input. Pair with GET /v1/responses/{id}."
              },
              {
                "question": "reasoning_effort (string, optional)",
                "answer": "\"minimal\", \"low\", \"medium\", or \"high\". Also accepts reasoning: { effort }."
              },
              {
                "question": "max_output_tokens (integer, optional)",
                "answer": "Maximum output tokens. Also accepts max_tokens for compatibility."
              },
              {
                "question": "temperature (number, optional)",
                "answer": "Sampling temperature."
              },
              {
                "question": "top_p (number, optional)",
                "answer": "Nucleus sampling parameter."
              },
              {
                "question": "stream (boolean, optional)",
                "answer": "Enable Responses-style server-sent events."
              },
              {
                "question": "tools (array, optional)",
                "answer": "Responses API function tools: { type: \"function\", name, description, parameters }."
              },
              {
                "question": "tool_choice (string | object, optional)",
                "answer": "\"auto\", \"none\", \"required\", or { type: \"function\", name: \"tool_name\" }."
              },
              {
                "question": "web_search (boolean, optional)",
                "answer": "Set to true (or pass web_search_options) to let the platform perform a live web search and supply the results to the model with citations. Only takes effect on models with capabilities.web_search: true (for example \"arya\"); ignored for other models. A plain-language request such as \"search the web for ...\" also triggers a search."
              }
            ]
          },
          {
            "type": "code",
            "code": "export OPENAI_BASE_URL=\"https://gab.ai/v1\"\nexport OPENAI_API_KEY=\"YOUR_GAB_API_KEY\"\n\ncodex",
            "language": "bash",
            "title": "Codex-style environment"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"input\": [\n    {\n      \"role\": \"user\",\n      \"content\": [\n        { \"type\": \"input_text\", \"text\": \"What is the weather in Paris?\" }\n      ]\n    }\n  ],\n  \"tools\": [\n    {\n      \"type\": \"function\",\n      \"name\": \"get_weather\",\n      \"description\": \"Get weather for a location.\",\n      \"parameters\": {\n        \"type\": \"object\",\n        \"properties\": {\n          \"location\": { \"type\": \"string\" }\n        },\n        \"required\": [\"location\"]\n      }\n    }\n  ],\n  \"tool_choice\": { \"type\": \"function\", \"name\": \"get_weather\" }\n}",
            "language": "json",
            "title": "Request with a function tool"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"resp_abc123\",\n  \"object\": \"response\",\n  \"status\": \"completed\",\n  \"model\": \"arya\",\n  \"output\": [\n    {\n      \"id\": \"fc_abc123\",\n      \"type\": \"function_call\",\n      \"status\": \"completed\",\n      \"call_id\": \"call_abc123\",\n      \"name\": \"get_weather\",\n      \"arguments\": \"{\\\\\"location\\\\\":\\\\\"Paris\\\\\"}\"\n    }\n  ],\n  \"usage\": {\n    \"input_tokens\": 80,\n    \"output_tokens\": 25,\n    \"total_tokens\": 105,\n    \"credits_used\": 1\n  }\n}",
            "language": "json",
            "title": "Response with function_call"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"arya\",\n  \"input\": [\n    {\n      \"role\": \"user\",\n      \"content\": [{ \"type\": \"input_text\", \"text\": \"What is the weather in Paris?\" }]\n    },\n    {\n      \"id\": \"fc_abc123\",\n      \"type\": \"function_call\",\n      \"call_id\": \"call_abc123\",\n      \"name\": \"get_weather\",\n      \"arguments\": \"{\\\\\"location\\\\\":\\\\\"Paris\\\\\"}\"\n    },\n    {\n      \"type\": \"function_call_output\",\n      \"call_id\": \"call_abc123\",\n      \"output\": \"{\\\\\"temperature\\\\\":22,\\\\\"unit\\\\\":\\\\\"celsius\\\\\",\\\\\"condition\\\\\":\\\\\"sunny\\\\\"}\"\n    }\n  ],\n  \"tools\": [\n    {\n      \"type\": \"function\",\n      \"name\": \"get_weather\",\n      \"description\": \"Get weather for a location.\",\n      \"parameters\": {\n        \"type\": \"object\",\n        \"properties\": { \"location\": { \"type\": \"string\" } },\n        \"required\": [\"location\"]\n      }\n    }\n  ]\n}",
            "language": "json",
            "title": "Follow-up with function_call_output"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Codex / OpenAI-compatible CLI setup",
            "copy": "Use https://gab.ai/v1 as the OpenAI base URL and your Gab AI API key as OPENAI_API_KEY. Clients that call /v1/responses can use function tools, streaming, and function call outputs."
          },
          {
            "type": "section",
            "title": "Anthropic Messages",
            "copy": "Gab AI also exposes an Anthropic-compatible Messages API for tools and SDKs that expect Anthropic's native format, including Claude Code. Use the root base URL https://gab.ai for Anthropic-compatible clients because they append /v1/messages themselves.\n\ncache_control on system, tools, and content blocks is honored. If the caller does not set a breakpoint, agentic requests auto-mark the last tool (and system text) as ephemeral. Usage includes cache_read_input_tokens when the provider reports a cache hit.\n\nAnthropic-compatible clients may call /v1/messages/count_tokens to estimate input tokens before sending a message. The response returns input_tokens.\n\nPOST /messages\n\nPOST /messages/count_tokens"
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body",
              "Count Tokens"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "model (string, required)",
                "answer": "Model ID. You may use a Gab slug such as \"claude-sonnet-4-5\", the provider model name such as \"claude-sonnet-4-5-20250929\", or \"auto\". When Auto is used, the response model field is the actual routed slug. POST /v1/messages/count_tokens accepts auto without creating route state."
              },
              {
                "question": "messages (array, required)",
                "answer": "Anthropic messages array. User/assistant messages use content blocks; assistant tool requests use type \"tool_use\"; tool results are user messages with type \"tool_result\"."
              },
              {
                "question": "system (string | array, optional)",
                "answer": "Anthropic system prompt. String content and text blocks are supported. cache_control on blocks is passed through; a string system is auto-marked as a cache breakpoint for agentic requests."
              },
              {
                "question": "thinking (object, optional)",
                "answer": "Anthropic extended thinking, e.g. { type: \"enabled\", budget_tokens: 8192 }. reasoning_effort on the request is mapped to thinking when this field is omitted."
              },
              {
                "question": "max_tokens (integer, optional)",
                "answer": "Maximum tokens to generate. Forwarded to Claude-compatible models."
              },
              {
                "question": "temperature (number, optional)",
                "answer": "Sampling temperature."
              },
              {
                "question": "top_p (number, optional)",
                "answer": "Nucleus sampling parameter."
              },
              {
                "question": "top_k (number, optional)",
                "answer": "Top-k sampling parameter for Anthropic models."
              },
              {
                "question": "stream (boolean, optional)",
                "answer": "Enable Anthropic-style server-sent events."
              },
              {
                "question": "tools (array, optional)",
                "answer": "Anthropic tool definitions with name, description, and input_schema. cache_control on a tool is passed through. If none is set, the last tool is auto-marked as a cache breakpoint."
              },
              {
                "question": "tool_choice (object, optional)",
                "answer": "Anthropic tool choice, such as { type: \"auto\" }, { type: \"any\" }, { type: \"none\" }, or { type: \"tool\", name: \"tool_name\" }."
              },
              {
                "question": "web_search (boolean, optional)",
                "answer": "Set to true (or pass web_search_options) to let the platform perform a live web search and supply the results to the model with citations. Only takes effect on models with capabilities.web_search: true (for example \"arya\"); ignored for models that lack the capability. A plain-language request such as \"search the web for ...\" also triggers a search."
              }
            ]
          },
          {
            "type": "code",
            "code": "export ANTHROPIC_BASE_URL=\"https://gab.ai\"\nexport ANTHROPIC_AUTH_TOKEN=\"YOUR_GAB_API_KEY\"\n\nclaude",
            "language": "bash",
            "title": "Claude Code"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"claude-sonnet-4-5\",\n  \"max_tokens\": 1024,\n  \"system\": \"You are a helpful coding assistant.\",\n  \"messages\": [\n    {\n      \"role\": \"user\",\n      \"content\": [\n        { \"type\": \"text\", \"text\": \"Read package.json and summarize the scripts.\" }\n      ]\n    }\n  ],\n  \"tools\": [\n    {\n      \"name\": \"read_file\",\n      \"description\": \"Read a file from the current workspace.\",\n      \"input_schema\": {\n        \"type\": \"object\",\n        \"properties\": {\n          \"path\": { \"type\": \"string\" }\n        },\n        \"required\": [\"path\"]\n      }\n    }\n  ],\n  \"tool_choice\": { \"type\": \"auto\" }\n}",
            "language": "json",
            "title": "Request with tools"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"msg_abc123\",\n  \"type\": \"message\",\n  \"role\": \"assistant\",\n  \"model\": \"claude-sonnet-4-5-20250929\",\n  \"content\": [\n    {\n      \"type\": \"tool_use\",\n      \"id\": \"toolu_abc123\",\n      \"name\": \"read_file\",\n      \"input\": { \"path\": \"package.json\" }\n    }\n  ],\n  \"stop_reason\": \"tool_use\",\n  \"stop_sequence\": null,\n  \"usage\": {\n    \"input_tokens\": 120,\n    \"output_tokens\": 32\n  }\n}",
            "language": "json",
            "title": "Response with tool_use"
          },
          {
            "type": "code",
            "code": "{\n  \"model\": \"claude-sonnet-4-5\",\n  \"max_tokens\": 1024,\n  \"messages\": [\n    {\n      \"role\": \"user\",\n      \"content\": [{ \"type\": \"text\", \"text\": \"Read package.json and summarize the scripts.\" }]\n    },\n    {\n      \"role\": \"assistant\",\n      \"content\": [\n        {\n          \"type\": \"tool_use\",\n          \"id\": \"toolu_abc123\",\n          \"name\": \"read_file\",\n          \"input\": { \"path\": \"package.json\" }\n        }\n      ]\n    },\n    {\n      \"role\": \"user\",\n      \"content\": [\n        {\n          \"type\": \"tool_result\",\n          \"tool_use_id\": \"toolu_abc123\",\n          \"content\": \"{\\\\\"scripts\\\\\":{\\\\\"start\\\\\":\\\\\"node server.js\\\\\"}}\"\n        }\n      ]\n    }\n  ],\n  \"tools\": [\n    {\n      \"name\": \"read_file\",\n      \"description\": \"Read a file from the current workspace.\",\n      \"input_schema\": {\n        \"type\": \"object\",\n        \"properties\": { \"path\": { \"type\": \"string\" } },\n        \"required\": [\"path\"]\n      }\n    }\n  ]\n}",
            "language": "json",
            "title": "Follow-up with tool_result"
          },
          {
            "type": "code",
            "code": "curl https://gab.ai/v1/messages/count_tokens \\\\\n  -H \"Content-Type: application/json\" \\\\\n  -H \"x-api-key: YOUR_GAB_API_KEY\" \\\\\n  -d '{\n    \"model\": \"claude-sonnet-4-5\",\n    \"messages\": [\n      { \"role\": \"user\", \"content\": [{ \"type\": \"text\", \"text\": \"Hello!\" }] }\n    ]\n  }'",
            "language": "bash",
            "title": "Count tokens"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Claude Code setup",
            "copy": "Set ANTHROPIC_BASE_URL to https://gab.ai and ANTHROPIC_AUTH_TOKEN to your Gab AI API key. Do not include /v1 in the base URL for Claude Code."
          },
          {
            "type": "section",
            "title": "Streaming Responses",
            "copy": "Set stream: true to receive responses as server-sent events (SSE) for real-time output:"
          },
          {
            "type": "code",
            "code": "const response = await fetch('https://gab.ai/v1/chat/completions', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'Bearer YOUR_API_KEY'\n  },\n  body: JSON.stringify({\n    model: 'arya',\n    messages: [{ role: 'user', content: 'Tell me a story' }],\n    stream: true\n  })\n});\n\nconst reader = response.body.getReader();\nconst decoder = new TextDecoder();\n\nwhile (true) {\n  const { done, value } = await reader.read();\n  if (done) break;\n  \n  const chunk = decoder.decode(value);\n  // Parse SSE data and handle tokens\n  console.log(chunk);\n}",
            "language": "javascript",
            "title": "Streaming Example"
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "OpenAI SDK Support",
            "copy": "The OpenAI SDK handles streaming automatically. Just set stream: true and iterate over the response."
          },
          {
            "type": "section",
            "title": "Embeddings",
            "copy": "Generate vector embeddings for text inputs. Embeddings are useful for semantic search, retrieval-augmented generation (RAG), clustering, and classification tasks. The endpoint shape mirrors OpenAI's /v1/embeddings.\n\nPOST /embeddings"
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "model (string, required)",
                "answer": "Embedding model ID — must be a slug from /v1/models?type=embedding."
              },
              {
                "question": "input (string | array, required)",
                "answer": "Text to embed. A single string or an array of strings (max 2048 items)."
              },
              {
                "question": "dimensions (integer, optional)",
                "answer": "Output vector dimensions. Forwarded to the upstream provider for models that support reduced dimensionality."
              },
              {
                "question": "encoding_format (string, optional)",
                "answer": "Encoding format: \"float\" (default) or \"base64\""
              }
            ]
          },
          {
            "type": "code",
            "code": "curl \"https://gab.ai/v1/models?type=embedding\" \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\\\n  | jq '.data[].id'",
            "language": "bash",
            "title": "Discover the current embedding model"
          },
          {
            "type": "code",
            "code": "const response = await fetch('https://gab.ai/v1/embeddings', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'Bearer YOUR_API_KEY'\n  },\n  body: JSON.stringify({\n    model: 'text-embedding-3-small',\n    input: 'The quick brown fox jumps over the lazy dog'\n  })\n});",
            "language": "javascript",
            "title": "Request Example"
          },
          {
            "type": "code",
            "code": "from openai import OpenAI\n\nclient = OpenAI(\n    api_key=\"YOUR_API_KEY\",\n    base_url=\"https://gab.ai/v1\"\n)\n\nresponse = client.embeddings.create(\n    model=\"text-embedding-3-small\",\n    input=[\"First document\", \"Second document\"]\n)\n\nfor item in response.data:\n    print(f\"Index {item.index}: {len(item.embedding)} dimensions\")",
            "language": "python",
            "title": "Python Example"
          },
          {
            "type": "code",
            "code": "{\n  \"object\": \"list\",\n  \"data\": [\n    {\n      \"object\": \"embedding\",\n      \"embedding\": [0.0023, -0.0091, 0.0152, ...],\n      \"index\": 0\n    }\n  ],\n  \"model\": \"text-embedding-3-small\",\n  \"usage\": {\n    \"prompt_tokens\": 9,\n    \"total_tokens\": 9,\n    \"credits_used\": 1\n  }\n}",
            "language": "json",
            "title": "Response Example"
          },
          {
            "type": "tip",
            "tipType": "warning",
            "title": "Embedding models",
            "copy": "Use text-embedding-3-small (default, 1536 dimensions) or text-embedding-3-large (3072 dimensions). List them with GET /v1/models?type=embedding. Aliases such as openai/text-embedding-3-small also resolve."
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Batching",
            "copy": "Pass an array of strings as input to embed multiple texts in a single request (up to 2048 items). This is significantly faster than making individual requests."
          },
          {
            "type": "section",
            "title": "Check Credits",
            "copy": "Check your current credit balance including monthly allotment and purchased credits.\n\nGET /credits"
          },
          {
            "type": "code",
            "code": "curl https://gab.ai/v1/credits \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "Request Example"
          },
          {
            "type": "code",
            "code": "{\n  \"object\": \"credit_balance\",\n  \"monthly_credits\": 2000,\n  \"monthly_used\": 450,\n  \"monthly_remaining\": 1550,\n  \"purchased_credits\": 500,\n  \"total_available\": 2050,\n  \"resets_at\": \"2026-02-01T00:00:00Z\"\n}",
            "language": "json",
            "title": "Response Example"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Credit Types",
            "copy": "Monthly credits reset at the start of each billing cycle. Purchased credits never expire and are used after monthly credits are exhausted."
          },
          {
            "type": "section",
            "title": "Usage History",
            "copy": "Fetch a per-request log of your API usage. Each record is a single billable transaction with its own tx_id, model, token counts and credit cost — useful for auditing billing, building dashboards, or reconciling spend. Records are returned newest-first.\n\nGET /usage"
          },
          {
            "type": "unordered-list",
            "items": [
              "Query Parameters"
            ]
          },
          {
            "type": "faq",
            "title": "Query Parameters",
            "items": [
              {
                "question": "after (string, optional)",
                "answer": "tx_id cursor — return records strictly newer than this one. When polling, pass the previous page's first_id (the newest tx in that response, same as data[0].id) — not last_id."
              },
              {
                "question": "before (string, optional)",
                "answer": "tx_id cursor — return records strictly older than this one. Use to walk backward through history."
              },
              {
                "question": "start_date (string | number, optional)",
                "answer": "Inclusive lower bound on created time. ISO 8601 (e.g. \"2026-04-01\") or unix timestamp (seconds or milliseconds)."
              },
              {
                "question": "end_date (string | number, optional)",
                "answer": "Inclusive upper bound on created time. Same formats as start_date."
              },
              {
                "question": "endpoint (string, optional)",
                "answer": "Filter to a specific endpoint, e.g. \"/v1/chat/completions\"."
              },
              {
                "question": "model (string, optional)",
                "answer": "Filter to a specific model slug (e.g. \"arya\")."
              },
              {
                "question": "limit (integer, optional)",
                "answer": "Records per page. 1–1000, default 100."
              }
            ]
          },
          {
            "type": "code",
            "code": "# Last 100 requests\ncurl https://gab.ai/v1/usage \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"\n\n# Poll for new activity since the last tx_id you've seen\ncurl \"https://gab.ai/v1/usage?after=65f2a1b3c4d5e6f7a8b9c0d1\" \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"\n\n# All chat.completions usage for March 2026\ncurl \"https://gab.ai/v1/usage?start_date=2026-03-01&end_date=2026-03-31&endpoint=/v1/chat/completions&limit=1000\" \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "Request Examples"
          },
          {
            "type": "code",
            "code": "{\n  \"object\": \"list\",\n  \"data\": [\n    {\n      \"id\": \"65f2a1b3c4d5e6f7a8b9c0d1\",\n      \"object\": \"usage_record\",\n      \"created\": 1712345678,\n      \"endpoint\": \"/v1/chat/completions\",\n      \"model\": \"arya\",\n      \"credits_used\": 1,\n      \"tokens\": {\n        \"prompt\": 842,\n        \"completion\": 156,\n        \"total\": 998\n      },\n      \"context_tokens\": 842,\n      \"response_time_ms\": 1245,\n      \"status_code\": 200,\n      \"success\": true\n    }\n  ],\n  \"has_more\": false,\n  \"first_id\": \"65f2a1b3c4d5e6f7a8b9c0d1\",\n  \"last_id\": \"65f2a1b3c4d5e6f7a8b9c0d1\"\n}",
            "language": "json",
            "title": "Response Example"
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Polling pattern",
            "copy": "To efficiently tail new activity, persist the first_id from the previous response (the newest tx_id in that page) and pass it as after on the next call. Do not use last_id here: that field is the oldest item in the page, since results are returned newest-first."
          },
          {
            "type": "section",
            "title": "List Models",
            "copy": "List all available models. Authentication is optional. Optionally filter by type to get only text, image, video, audio, or embedding models. Each model includes aliases, context_window, max_output_tokens, capabilities, recommended_for, and credit_cost.\n\nGET /models\n\nGET /models/{id}"
          },
          {
            "type": "unordered-list",
            "items": [
              "Query Parameters"
            ]
          },
          {
            "type": "faq",
            "title": "Query Parameters",
            "items": [
              {
                "question": "type (string, optional)",
                "answer": "Filter by model type: \"text\", \"image\", \"video\", \"audio\", or \"embedding\""
              }
            ]
          },
          {
            "type": "code",
            "code": "# Get all models (key optional)\ncurl https://gab.ai/v1/models\n\n# Get only image models\ncurl \"https://gab.ai/v1/models?type=image\"\n\n# Look up a slug or alias\ncurl https://gab.ai/v1/models/gpt-5.5",
            "language": "bash",
            "title": "Request Examples"
          },
          {
            "type": "code",
            "code": "{\n  \"object\": \"list\",\n  \"data\": [\n    {\n      \"id\": \"claude-sonnet-4-5\",\n      \"object\": \"model\",\n      \"created\": 1700000000,\n      \"owned_by\": \"Anthropic\",\n      \"aliases\": [\"claude-sonnet-4.5\", \"anthropic/claude-sonnet-4-5\", \"anthropic/claude-sonnet-4.5\"],\n      \"recommended_for\": [\"coding\", \"agents\"],\n      \"capabilities\": {\n        \"text\": true,\n        \"streaming\": true,\n        \"thinking\": true,\n        \"function_calling\": true,\n        \"image_input\": true\n      },\n      \"context_window\": 200000,\n      \"max_output_tokens\": 64000,\n      \"credit_cost\": { \"base_cost\": 5 },\n      \"is_plus_only\": true\n    }\n  ]\n}",
            "language": "json",
            "title": "Response Example"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Model capabilities",
            "copy": "The capabilities object lists boolean flags for every feature the model supports. Notable flags for chat: text, streaming, function_calling, thinking, web_search, image_input, file_input. Pass tools only to models with function_calling: true — otherwise the request fails with unsupported_tool_calling. recommended_for includes \"coding\" on function-calling text models. aliases lists dotted versions and provider prefixes that resolve to the same id."
          },
          {
            "type": "section",
            "title": "Agent setup and OpenAPI",
            "copy": "GET /v1/agent-setup?client=cursor|opencode|openclaw|claude-code returns generated configs from the live catalog, including context windows and tool_call flags. The OpenCode config currently uses the v1 format with @ai-sdk/openai for /v1/responses. OpenCode v2 uses @opencode/ai/providers/openai-compatible/responses and a different config structure; see /docs/opencode. GET /openapi.json is the OpenAPI 3.1 spec. GET /llms-full.txt is a generated plain-text catalog for coding agents."
          },
          {
            "type": "code",
            "code": "curl \"https://gab.ai/v1/agent-setup?client=opencode\"\ncurl https://gab.ai/openapi.json\ncurl https://gab.ai/llms-full.txt",
            "language": "bash",
            "title": "Generated configs"
          },
          {
            "type": "section",
            "title": "Image Generation",
            "copy": "Generate images from text prompts using various AI image models. Each item in data includes a public url you can download and an id you can pass to delete the stored copy.\n\nAPI-generated images are not added to your Gab AI Files library in the app. The UUID in the CDN URL is the storage filename, not the file id — that is why DELETE on the URL filename alone used to fail.\n\nPOST /images/generations\n\nDELETE /images/:id\n\nDELETE /images?url="
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body",
              "Deleting generated media"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "model (string, optional)",
                "answer": "Image model slug (e.g., \"gpt-image-2\", \"gpt-image-1-mini\", \"image-generator\", \"nano-banana-2\", \"seedream-4-5\", \"seedream-4-0\", \"qwen-image-2\", \"qwen-image\", \"flux-2-pro\"). Defaults to \"gpt-image-1-mini\". Use GET /v1/models?type=image for the live list."
              },
              {
                "question": "prompt (string, required)",
                "answer": "Text description of the image to generate. Maximum length is model-specific (characters). If exceeded, the API returns 400 with code prompt_too_long. Use GET /v1/models?type=image and max_prompt_characters on each model (e.g. image-generator: 2,048; gpt-image-2: 32,000)."
              },
              {
                "question": "n (integer, optional)",
                "answer": "Number of images to generate (1-4, default 1). Some models cap n=1 internally."
              },
              {
                "question": "size (string, optional)",
                "answer": "Image size, model-dependent. Common values: \"1024x1024\", \"1792x1024\", \"1024x1792\"."
              },
              {
                "question": "quality (string, optional)",
                "answer": "Quality level: \"standard\" (default) or \"hd\". Forwarded to providers that support it."
              }
            ]
          },
          {
            "type": "code",
            "code": "const response = await fetch('https://gab.ai/v1/images/generations', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'Bearer YOUR_API_KEY'\n  },\n  body: JSON.stringify({\n    model: 'gpt-image-2',\n    prompt: 'A serene mountain landscape at sunset with a lake reflection',\n    n: 1,\n    size: '1024x1024',\n    quality: 'hd'\n  })\n});",
            "language": "javascript",
            "title": "Request Example"
          },
          {
            "type": "code",
            "code": "{\n  \"created\": 1704067200,\n  \"data\": [\n    {\n      \"id\": \"6650f0a1b2c3d4e5f6a7b8c9\",\n      \"url\": \"https://cdn.gab.ai/users/USER_ID/uploads/30066743-5e4a-4b8c-b4cd-1b41c41caa68.png\",\n      \"revised_prompt\": \"A serene mountain landscape at sunset...\"\n    }\n  ],\n  \"usage\": {\n    \"credits_used\": 5\n  }\n}",
            "language": "json",
            "title": "Response Example"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Not saved to the Files library",
            "copy": "Media created through the API (images, edits, video, audio, 3D) is stored so the CDN url works, but it does not appear in the in-app Files library. Download the url for your own app, then optionally delete the stored copy. GET /v1/files lists API-created files if you need the id later."
          },
          {
            "type": "code",
            "code": "curl -X DELETE https://gab.ai/v1/images/6650f0a1b2c3d4e5f6a7b8c9 \\\n  -H \"Authorization: Bearer YOUR_API_KEY\"\n\n# Same effect via the files endpoint:\ncurl -X DELETE https://gab.ai/v1/files/6650f0a1b2c3d4e5f6a7b8c9 \\\n  -H \"Authorization: Bearer YOUR_API_KEY\"\n\n# Older images that only returned a URL — pass the full CDN URL:\ncurl -G -X DELETE https://gab.ai/v1/images \\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\n  --data-urlencode \"url=https://cdn.gab.ai/users/USER_ID/uploads/30066743-5e4a-4b8c-b4cd-1b41c41caa68.png\"",
            "language": "bash",
            "title": "Delete a generated image"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"6650f0a1b2c3d4e5f6a7b8c9\",\n  \"object\": \"image\",\n  \"deleted\": true\n}",
            "language": "json",
            "title": "Delete Response"
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Image Models",
            "copy": "Use /v1/models?type=image to see all available image generation models, their capabilities, and max_prompt_characters per model. Long structured text (recipes, articles) often exceeds smaller models — summarize to a short visual description or use gpt-image-2."
          },
          {
            "type": "tip",
            "tipType": "warning",
            "title": "Need a reference / input image?",
            "copy": "/v1/images/generations is text-to-image only — an image, images, or image_url field here is rejected. To pass a source or reference image (character consistency, restyles, compositing), use POST /v1/images/edit below. Image-only models like flux-2-pro also cannot be called via /v1/chat/completions."
          },
          {
            "type": "section",
            "title": "Image Editing (Reference Images)",
            "copy": "Edit one or more existing images from a text prompt — the API equivalent of attaching a reference image in the Gab web UI. Use this endpoint whenever the model advertises capabilities.image_input: true (for example flux-2-pro) and you need the model to see a source image.\n\nSupply source images either as multipart uploads on the image field, or as file id(s) previously returned by POST /v1/files with purpose \"vision\". CDN URLs and base64 are not accepted on this endpoint; upload first, then pass the file id.\n\nPOST /images/edit (also accepts POST /images/edits)\n\nGET /images/edits/:jobId"
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body",
              "Async mode"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "prompt (string, required)",
                "answer": "Text description of the edit or reference-guided generation (e.g. \"Generate this same character waving\")."
              },
              {
                "question": "image (file | string | string[], required)",
                "answer": "Source image(s). Multipart: repeat the image field (up to 10). JSON: a file id string, or an array of file ids, from POST /v1/files with purpose \"vision\"."
              },
              {
                "question": "model (string, optional)",
                "answer": "Image model slug. Defaults to \"gpt-image-1-mini\". Recommended for edits: \"gpt-image-1-mini\", \"gpt-image-2\", \"nano-banana-2\", \"flux-2-pro\". Use GET /v1/models?type=image for the live list."
              },
              {
                "question": "n (integer, optional)",
                "answer": "Number of edited images to return (1-4, default 1)."
              },
              {
                "question": "size (string, optional)",
                "answer": "Output image size. Default: \"1024x1024\"."
              },
              {
                "question": "quality (string, optional)",
                "answer": "\"standard\" (default) or \"hd\"."
              },
              {
                "question": "async (boolean, optional)",
                "answer": "When true, returns 202 immediately with a job id; poll GET /v1/images/edits/:jobId. background is accepted as an alias. Recommended for longer edits (flux-2-pro, gpt-image-2, large sizes) to avoid gateway timeouts."
              }
            ]
          },
          {
            "type": "code",
            "code": "# 1) Upload the reference image\ncurl https://gab.ai/v1/files \\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\n  -F \"purpose=vision\" \\\n  -F \"file=@./character.png\"\n# -> { \"id\": \"6a69157fce666acc87cba76f\", ... }\n\n# 2) Edit / generate with that reference (async recommended)\ncurl https://gab.ai/v1/images/edit \\\n  -H \"Content-Type: application/json\" \\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\n  -d '{\n    \"model\": \"flux-2-pro\",\n    \"prompt\": \"Generate this same character waving\",\n    \"image\": \"6a69157fce666acc87cba76f\",\n    \"async\": true\n  }'\n# -> 202 { \"id\": \"<jobId>\", \"status\": \"processing\", ... }\n\n# 3) Poll until completed\ncurl https://gab.ai/v1/images/edits/<jobId> \\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "Reference image with flux-2-pro (JSON + file id)"
          },
          {
            "type": "code",
            "code": "curl https://gab.ai/v1/images/edit \\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\n  -F \"model=flux-2-pro\" \\\n  -F \"prompt=Generate this same character waving\" \\\n  -F \"async=true\" \\\n  -F \"image=@./character.png\"",
            "language": "bash",
            "title": "Multipart upload (no prior /v1/files call)"
          },
          {
            "type": "code",
            "code": "const response = await fetch('https://gab.ai/v1/images/edit', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'Bearer YOUR_API_KEY'\n  },\n  body: JSON.stringify({\n    model: 'flux-2-pro',\n    prompt: 'Generate this same character waving',\n    image: '6a69157fce666acc87cba76f', // file id from POST /v1/files\n    async: true\n  })\n});",
            "language": "javascript",
            "title": "Request Example (JSON)"
          },
          {
            "type": "code",
            "code": "{\n  \"created\": 1704067200,\n  \"data\": [\n    {\n      \"id\": \"6650f0a1b2c3d4e5f6a7b8c9\",\n      \"url\": \"https://cdn.gab.ai/users/USER_ID/uploads/edited123.png\",\n      \"revised_prompt\": \"Generate this same character waving...\"\n    }\n  ],\n  \"usage\": {\n    \"credits_used\": 5\n  }\n}",
            "language": "json",
            "title": "Synchronous Response"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Async mode",
            "copy": "Image edits often take 40–120+ seconds. Pass async: true to get a 202 with a job id, then poll GET /v1/images/edits/:jobId until status is completed or failed. Synchronous requests that exceed edge timeouts may surface as an HTML 504 even though the edit is still running."
          },
          {
            "type": "section",
            "title": "Files",
            "copy": "Upload a file once and reference it by file_id in other API calls — as an image input to /v1/chat/completions, or as the image field on /v1/images/edit. Files are scoped to the authenticated user (the owner of the API key) and are not accessible to other users. The response shape is OpenAI-compatible.\n\nAPI uploads and API-generated media are not shown in the in-app Files library. GET /v1/files lists those API-created files. Delete generated images with DELETE /v1/images/:id or DELETE /v1/files/:id. Both accept the File id from the generation response, the UUID filename from the CDN URL, or the full CDN URL via ?url=.\n\nUpload a file using multipart/form-data. Returns a file object with an id you can use as file_id in subsequent requests.\n\nList files created through the API for the current user. Optionally filter by purpose.\n\nRetrieve metadata for a single file. fileId may be the File id, the storage filename, or a URL-encoded CDN URL.\n\nDelete a file. Removes the stored object and marks the file as deleted. Any outstanding references (e.g., file_id in a chat message) will fail with file_not_found after deletion.\n\nPOST /files\n\nGET /files\n\nGET /files/:fileId\n\nDELETE /files/:fileId\n\nDELETE /images/:id"
          },
          {
            "type": "unordered-list",
            "items": [
              "Form Fields",
              "Query Parameters"
            ]
          },
          {
            "type": "faq",
            "title": "Form Fields",
            "items": [
              {
                "question": "file (file, required)",
                "answer": "The binary file to upload (multipart field \"file\")."
              },
              {
                "question": "purpose (string, optional)",
                "answer": "What the file will be used for: \"vision\" (image for chat completions or /v1/images/edit), \"assistants\", \"user_data\" (default), \"batch\", or \"fine-tune\". Purpose \"vision\" requires an image file."
              }
            ]
          },
          {
            "type": "faq",
            "title": "Query Parameters",
            "items": [
              {
                "question": "limit (integer, optional)",
                "answer": "Page size (1-100, default 20)."
              },
              {
                "question": "purpose (string, optional)",
                "answer": "Filter by purpose (e.g., \"vision\")."
              }
            ]
          },
          {
            "type": "code",
            "code": "curl -X POST https://gab.ai/v1/files \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\\\n  -F \"purpose=vision\" \\\\\n  -F \"file=@./photo.png\"",
            "language": "bash",
            "title": "Upload with curl"
          },
          {
            "type": "code",
            "code": "const form = new FormData();\nform.append('purpose', 'vision');\nform.append('file', fileBlob, 'photo.png');\n\nconst res = await fetch('https://gab.ai/v1/files', {\n  method: 'POST',\n  headers: { 'Authorization': 'Bearer YOUR_API_KEY' },\n  body: form,\n});\nconst uploaded = await res.json();\n// uploaded.id — use as file_id in /v1/chat/completions, or as \"image\" on /v1/images/edit",
            "language": "javascript",
            "title": "Upload with fetch"
          },
          {
            "type": "code",
            "code": "from openai import OpenAI\n\nclient = OpenAI(api_key=\"YOUR_API_KEY\", base_url=\"https://gab.ai/v1\")\n\nuploaded = client.files.create(\n    file=open(\"photo.png\", \"rb\"),\n    purpose=\"vision\",\n)\n\nchat = client.chat.completions.create(\n    model=\"arya\",\n    messages=[{\n        \"role\": \"user\",\n        \"content\": [\n            {\"type\": \"text\", \"text\": \"What's in this image?\"},\n            {\"type\": \"image_url\", \"image_url\": {\"file_id\": uploaded.id}},\n        ],\n    }],\n)\nprint(chat.choices[0].message.content)",
            "language": "python",
            "title": "Upload with OpenAI SDK"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"67a1b2c3d4e5f6a7b8c9d0e1\",\n  \"object\": \"file\",\n  \"bytes\": 184523,\n  \"created_at\": 1704067200,\n  \"filename\": \"photo.png\",\n  \"purpose\": \"vision\",\n  \"mime_type\": \"image/png\",\n  \"file_type\": \"image\",\n  \"url\": \"https://cdn.gab.ai/users/abc/files/photo.png\",\n  \"status\": \"active\"\n}",
            "language": "json",
            "title": "Response"
          },
          {
            "type": "code",
            "code": "curl \"https://gab.ai/v1/files?purpose=vision&limit=50\" \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "List files"
          },
          {
            "type": "code",
            "code": "curl https://gab.ai/v1/files/FILE_ID \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "Retrieve file"
          },
          {
            "type": "code",
            "code": "curl -X DELETE https://gab.ai/v1/files/FILE_ID \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "Delete file"
          },
          {
            "type": "code",
            "code": "{ \"id\": \"FILE_ID\", \"object\": \"file\", \"deleted\": true }",
            "language": "json",
            "title": "Response"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Limits",
            "copy": "Per-file upload limit is 50 MB. Individual types have their own caps in downstream processing (e.g., Whisper transcription is capped at 25 MB). Image types accepted: PNG, JPEG, GIF, WebP, HEIC, SVG."
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "API files vs the app library",
            "copy": "Files uploaded or generated through the API do not appear in Gab AI's in-app Files page. List them with GET /v1/files and delete them with DELETE /v1/files/:id or DELETE /v1/images/:id. The CDN URL's filename is a storage UUID, not the File id."
          },
          {
            "type": "section",
            "title": "Video Generation",
            "copy": "Generate videos from text prompts. Video generation is asynchronous—you'll receive a job ID and poll for the result.\n\nStart a video generation job. Returns a job ID to poll for completion.\n\nCheck the status of a video generation job. Poll this endpoint until status is \"completed\" or \"failed\".\n\nPOST /videos/generations\n\nGET /videos/:jobId"
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "model (string, optional)",
                "answer": "Video model slug (e.g., \"veo-3-1\", \"veo-3-1-fast\", \"kling-3-0-pro\", \"kling-2-5-turbo-pro\", \"hailuo-2-3-pro\", \"wan-2-5\", \"seedance-2-0\"). Defaults to \"veo-3-1-fast\". Use GET /v1/models?type=video for the live list."
              },
              {
                "question": "prompt (string, required)",
                "answer": "Text description of the video to generate"
              },
              {
                "question": "duration (integer, optional)",
                "answer": "Video duration in seconds (model dependent)"
              },
              {
                "question": "aspect_ratio (string, optional)",
                "answer": "Aspect ratio: \"16:9\", \"9:16\", \"1:1\""
              }
            ]
          },
          {
            "type": "code",
            "code": "const response = await fetch('https://gab.ai/v1/videos/generations', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'Bearer YOUR_API_KEY'\n  },\n  body: JSON.stringify({\n    model: 'veo-3-1-fast',\n    prompt: 'A golden retriever running through a field of flowers',\n    duration: 5,\n    aspect_ratio: '16:9'\n  })\n});",
            "language": "javascript",
            "title": "Start Generation"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"video_job_xyz789\",\n  \"object\": \"video.generation\",\n  \"status\": \"processing\",\n  \"created\": 1704067200,\n  \"model\": \"veo-3-1-fast\",\n  \"prompt\": \"A golden retriever running through a field of flowers\",\n  \"usage\": {\n    \"credits_used\": 20\n  }\n}",
            "language": "json",
            "title": "Response (HTTP 202 — job queued)"
          },
          {
            "type": "code",
            "code": "curl https://gab.ai/v1/videos/video_job_xyz789 \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "Poll Status"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"video_job_xyz789\",\n  \"object\": \"video.generation\",\n  \"status\": \"completed\",\n  \"created\": 1704067200,\n  \"completed_at\": 1704067320,\n  \"model\": \"veo-3-1-fast\",\n  \"data\": [\n    {\n      \"id\": \"6650f0a1b2c3d4e5f6a7b8c9\",\n      \"url\": \"https://cdn.gab.ai/users/USER_ID/uploads/xyz789.mp4\",\n      \"duration\": 5,\n      \"thumbnail\": \"https://cdn.gab.ai/videos/thumbnails/xyz789.jpg\"\n    }\n  ]\n}",
            "language": "json",
            "title": "Completed Response"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Not saved to the Files library",
            "copy": "Completed videos are not added to the in-app Files library. Use data[].id with DELETE /v1/files/:id (or the UUID / full CDN URL) if you want to remove the stored copy after download."
          },
          {
            "type": "tip",
            "tipType": "warning",
            "title": "Processing Time",
            "copy": "Video generation typically takes 1-3 minutes depending on duration and model. Implement exponential backoff when polling to avoid rate limits."
          },
          {
            "type": "section",
            "title": "Text-to-Speech",
            "copy": "Convert text to natural-sounding speech. Unlike OpenAI's TTS endpoint, Gab AI returns a JSON object with a CDN URL to the generated audio (rather than streaming raw audio bytes). You can fetch the URL to download the file.\n\nPOST /audio/speech"
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "model (string, optional)",
                "answer": "TTS model slug. Currently \"elevenlabs-tts-v4\" (the default if omitted); \"elevenlabs-tts-v3\", \"qwen-audio-3-0-tts-flash\" and \"gpt-4o-mini-tts\" are also available. Use GET /v1/models?type=audio to discover others."
              },
              {
                "question": "input (string, required)",
                "answer": "The text to convert to speech (max 4096 chars)"
              },
              {
                "question": "voice (string, optional)",
                "answer": "Voice ID. For elevenlabs-tts-v4: \"Rachel\" (default), \"Roger\", \"Aria\", \"Sarah\" and other ElevenLabs voices listed by GET /v1/models. For qwen-audio-3-0-tts-flash: \"longanhuan_v3.6\", \"loongjohn\". For gpt-4o-mini-tts: \"alloy\", \"ash\", \"ballad\", \"coral\", \"echo\", \"fable\", \"nova\", \"onyx\", \"sage\", \"shimmer\", \"verse\"."
              },
              {
                "question": "response_format (string, optional)",
                "answer": "Audio format: \"mp3\" (default), \"opus\", \"aac\", \"flac\"."
              },
              {
                "question": "speed (number, optional)",
                "answer": "Speaking speed (0.25 to 4.0, default 1.0)"
              },
              {
                "question": "instructions (string, optional)",
                "answer": "Optional voice/style instructions forwarded to the model (e.g., \"Speak in a calm, slow voice\")."
              }
            ]
          },
          {
            "type": "code",
            "code": "const response = await fetch('https://gab.ai/v1/audio/speech', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'Authorization': 'Bearer YOUR_API_KEY'\n  },\n  body: JSON.stringify({\n    model: 'elevenlabs-tts-v4',\n    input: 'Hello! Welcome to Gab AI. How can I help you today?',\n    voice: 'Rachel',\n    response_format: 'mp3'\n  })\n});\n\nconst { url } = await response.json();\n// Fetch the generated audio\nconst audio = await fetch(url).then(r => r.blob());\nconst audioUrl = URL.createObjectURL(audio);",
            "language": "javascript",
            "title": "Request Example"
          },
          {
            "type": "code",
            "code": "import requests\n\nresp = requests.post(\n    \"https://gab.ai/v1/audio/speech\",\n    headers={\"Authorization\": \"Bearer YOUR_API_KEY\"},\n    json={\n        \"model\": \"elevenlabs-tts-v4\",\n        \"voice\": \"Rachel\",\n        \"input\": \"Hello! Welcome to Gab AI.\",\n    },\n).json()\n\n# Download the generated audio\nwith open(\"output.mp3\", \"wb\") as f:\n    f.write(requests.get(resp[\"url\"]).content)",
            "language": "python",
            "title": "Python Example (raw HTTP — not the OpenAI SDK)"
          },
          {
            "type": "code",
            "code": "{\n  \"url\": \"https://cdn.gab.ai/audio/generated/abc123.mp3\",\n  \"content_type\": \"audio/mp3\",\n  \"usage\": { \"credits_used\": 1 }\n}",
            "language": "json",
            "title": "Response Example"
          },
          {
            "type": "tip",
            "tipType": "warning",
            "title": "Differs from OpenAI's SDK",
            "copy": "OpenAI's client.audio.speech.create(...) expects raw audio bytes back, so calling Gab AI through the OpenAI Python SDK with response.stream_to_file() will not work. Use raw HTTP (or fetch the returned url after parsing JSON) instead."
          },
          {
            "type": "section",
            "title": "API Key Management",
            "copy": "Programmatically manage your API keys. List existing keys, create new ones, or revoke keys that are no longer needed.\n\nList all API keys associated with your account. Keys are returned with masked values for security.\n\nCreate a new API key. The full key is only shown once in the response—store it securely.\n\nRevoke an API key. The key will immediately stop working for all requests.\n\nGET /api-keys\n\nPOST /api-keys\n\nDELETE /api-keys/:keyId"
          },
          {
            "type": "unordered-list",
            "items": [
              "Request Body"
            ]
          },
          {
            "type": "faq",
            "title": "Request Body",
            "items": [
              {
                "question": "name (string, optional)",
                "answer": "A friendly name for the key (e.g., \"Production\", \"Dev Server\")"
              }
            ]
          },
          {
            "type": "code",
            "code": "curl https://gab.ai/v1/api-keys \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "Request"
          },
          {
            "type": "code",
            "code": "{\n  \"object\": \"list\",\n  \"data\": [\n    {\n      \"id\": \"65f2a1b3c4d5e6f7a8b9c0d1\",\n      \"object\": \"api_key\",\n      \"key\": \"gab_5e17...d85f\",\n      \"name\": \"Production Key\",\n      \"is_active\": true,\n      \"created\": 1704067200,\n      \"last_used\": 1704672000,\n      \"usage\": { \"requests\": 4815, \"tokens\": 162342 }\n    },\n    {\n      \"id\": \"65f2a1b3c4d5e6f7a8b9c0d2\",\n      \"object\": \"api_key\",\n      \"key\": \"gab_a1b2...c3d4\",\n      \"name\": \"Development Key\",\n      \"is_active\": true,\n      \"created\": 1704153600,\n      \"last_used\": null,\n      \"usage\": { \"requests\": 0, \"tokens\": 0 }\n    }\n  ]\n}",
            "language": "json",
            "title": "Response"
          },
          {
            "type": "code",
            "code": "curl -X POST https://gab.ai/v1/api-keys \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\\\n  -H \"Content-Type: application/json\" \\\\\n  -d '{\"name\": \"New Production Key\"}'",
            "language": "bash",
            "title": "Request"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"65f2a1b3c4d5e6f7a8b9c0d3\",\n  \"object\": \"api_key\",\n  \"key\": \"gab-5e17f695110d1e02c90e4537445d85fce4f5...\",\n  \"name\": \"New Production Key\",\n  \"is_active\": true,\n  \"created\": 1704067200,\n  \"message\": \"Save this key securely. It will not be shown again.\"\n}",
            "language": "json",
            "title": "Response (HTTP 201)"
          },
          {
            "type": "code",
            "code": "curl -X DELETE https://gab.ai/v1/api-keys/65f2a1b3c4d5e6f7a8b9c0d1 \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\"",
            "language": "bash",
            "title": "Request"
          },
          {
            "type": "code",
            "code": "{\n  \"id\": \"65f2a1b3c4d5e6f7a8b9c0d1\",\n  \"object\": \"api_key\",\n  \"deleted\": true\n}",
            "language": "json",
            "title": "Response"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Key prefix",
            "copy": "Keys created via this endpoint use the prefix gab- (followed by 64 hex chars). Keys created via the in-app Settings UI use the prefix gab_ (followed by 32 hex chars). Both forms are valid as Authorization: Bearer tokens — treat the entire string as opaque."
          },
          {
            "type": "tip",
            "tipType": "warning",
            "title": "Save Your Key",
            "copy": "The full API key is only returned once when created. Store it securely—you won't be able to retrieve it again."
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Self-deletion blocked",
            "copy": "You cannot delete the API key being used to authenticate the request — the server returns 400 cannot_delete_current_key. Use a different active key (or the dashboard) to revoke it. Deletion is a soft delete: the key is marked inactive and rejected from then on."
          },
          {
            "type": "section",
            "title": "Account Data Export",
            "copy": "Download a portable archive of every record we hold for the API key's owner — profile, conversations, memories, files, custom bots, collections, voice sessions, bookmark folders, automated tasks, purchases, credit purchases, referrals, and feedback. This is the same data that powers the in-app Settings → Download your data button, exposed here for backup tools, GDPR / CCPA workflows, and self-hosted analytics pipelines.\n\nThe ZIP wraps the same data in a structure that's easy to browse without writing any code. Schema version is pinned in the manifest so consumers can detect breaking changes.\n\nGET /account/export"
          },
          {
            "type": "unordered-list",
            "items": [
              "Query Parameters",
              "ZIP Layout (`format=zip`)"
            ]
          },
          {
            "type": "faq",
            "title": "Query Parameters",
            "items": [
              {
                "question": "format (string, optional)",
                "answer": "Either \"json\" (default for the developer API — one big JSON document) or \"zip\" (a user-friendly archive with per-collection JSON files, individual conversation markdown files, a README, and a manifest)."
              },
              {
                "question": "include_messages (boolean, optional)",
                "answer": "When set to \"false\", conversations are returned without their (often very large) messages array. Defaults to true so you get the full archive."
              }
            ]
          },
          {
            "type": "code",
            "code": "# JSON bundle (default)\ncurl https://gab.ai/v1/account/export \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\\\n  -o gab-ai-account-data.json\n\n# ZIP archive (same shape the Settings UI downloads)\ncurl \"https://gab.ai/v1/account/export?format=zip\" \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\\\n  -o gab-ai-account-data.zip\n\n# Slim JSON — metadata only, no message bodies\ncurl \"https://gab.ai/v1/account/export?include_messages=false\" \\\\\n  -H \"Authorization: Bearer YOUR_API_KEY\" \\\\\n  -o gab-ai-account-data-slim.json",
            "language": "bash",
            "title": "Request"
          },
          {
            "type": "code",
            "code": "{\n  \"object\": \"account_data_export\",\n  \"exported_at\": \"2026-05-02T18:00:00.000Z\",\n  \"user_id\": \"65f2a1b3c4d5e6f7a8b9c0d1\",\n  \"counts\": {\n    \"conversations\": 412,\n    \"memories\": 87,\n    \"files\": 23,\n    \"agents\": 4,\n    \"collections\": 6,\n    \"voice_sessions\": 18,\n    \"purchases\": 3,\n    \"credit_purchases\": 11,\n    \"referrals\": 2,\n    \"feedback\": 1,\n    \"inference_tasks\": 0,\n    \"bookmark_folders\": 5,\n    \"api_keys\": 2\n  },\n  \"truncated\": {},\n  \"data\": {\n    \"user\": { \"_id\": \"...\", \"email\": \"...\", \"username\": \"...\", \"...\": \"...\" },\n    \"conversations\": [ /* full conversation rows with messages */ ],\n    \"memories\": [ /* memory rows (embeddings stripped) */ ],\n    \"files\": [ /* file metadata + cdn paths */ ],\n    \"agents\": [ /* your custom bots */ ],\n    \"collections\": [ /* your collections */ ],\n    \"voice_sessions\": [ /* voice mode session metadata */ ],\n    \"purchases\": [ /* Plus subscription purchases */ ],\n    \"credit_purchases\": [ /* one-time credit packs */ ],\n    \"referrals\": [ /* referrals you sent or received */ ],\n    \"feedback\": [ /* feedback you've submitted */ ],\n    \"inference_tasks\": [ /* scheduled / recurring tasks */ ],\n    \"bookmark_folders\": [ /* bookmark organization */ ],\n    \"api_keys\": [ /* metadata only — secret values are never re-exported */ ]\n  }\n}",
            "language": "json",
            "title": "JSON Response Shape"
          },
          {
            "type": "code",
            "code": "gab-ai-account-data-2026-05-02.zip\n├── README.md                  # human-readable overview, counts, layout\n├── manifest.json              # exported_at, counts, truncated, schema_version\n├── profile.json               # your account profile\n├── conversations.json         # raw bundle of every conversation\n├── conversations/             # one .md file per conversation, easy to read\n│   ├── 2026-05-01-untitled-conversation-abc123.md\n│   └── ...\n├── memories.json              # raw memory bundle\n├── memories.txt               # one memory per line, easy to grep\n├── files.json\n├── agents.json\n├── collections.json\n├── voice_sessions.json\n├── bookmark_folders.json\n├── inference_tasks.json\n├── purchases.json\n├── credit_purchases.json\n├── referrals.json\n├── feedback.json\n└── api_keys.json              # metadata only",
            "language": "text",
            "title": "Archive contents"
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "What's excluded",
            "copy": "Deleted records and temporary (\"incognito\") chats are filtered out before we build the archive. We also strip credentials (passwords, password reset tokens, MFA secrets, backup codes), payment-processor IDs (Authorize.net / Valmar customer + payment-method IDs), memory embedding vectors, files attached to temporary chats, and the secret value of API keys. Everything else that belongs to your account is in the export."
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Truncation",
            "copy": "Each collection is capped at a generous limit (5,000 conversations, 10,000 files, etc.). If a collection trips its cap the JSON response includes the key in the top-level truncated object (and the ZIP's README spells it out). Email support@gab.ai for a complete offline archive if you need the full history."
          },
          {
            "type": "tip",
            "tipType": "warning",
            "title": "Rate limits",
            "copy": "The export endpoint is intentionally heavy. The same daily API rate limit applies, but in practice you should only need a handful of calls — cache the result locally rather than polling."
          },
          {
            "type": "section",
            "title": "Message Roles",
            "copy": "The messages array in chat completions supports these roles:"
          },
          {
            "type": "cards",
            "items": [
              {
                "icon": "",
                "title": "system",
                "description": "Sets the behavior and context for the assistant. API requests do NOT inherit the API key owner's profile (name, location, etc.), so if you want the assistant to know who the end user is, describe them here (e.g. \"You are chatting with Jane from Austin.\")."
              },
              {
                "icon": "",
                "title": "user",
                "description": "The human user's messages"
              },
              {
                "icon": "",
                "title": "assistant",
                "description": "Previous AI responses (for context). May include tool_calls: array of { '{ id, type: \"function\", function: { name, arguments } }' } when using tools."
              },
              {
                "icon": "",
                "title": "tool",
                "description": "Tool result messages. Must include tool_call_id (matching the tool call id) and content (the tool’s result string)."
              }
            ]
          },
          {
            "type": "unordered-list",
            "items": [
              "Sets the behavior and context for the assistant. API requests do NOT inherit the API key owner's profile (name, location, etc.), so if you want the assistant to know who the end user is, describe them here (e.g. \"You are chatting with Jane from Austin.\").",
              "The human user's messages",
              "Previous AI responses (for context). May include tool_calls: array of { '{ id, type: \"function\", function: { name, arguments } }' } when using tools.",
              "Tool result messages. Must include tool_call_id (matching the tool call id) and content (the tool’s result string)."
            ]
          },
          {
            "type": "section",
            "title": "Error Codes",
            "copy": "All errors follow a consistent format with an error object containing message, type, and code:\n\nThe error.code field gives a stable machine-readable identifier independent of the HTTP status. The most common ones:"
          },
          {
            "type": "unordered-list",
            "items": [
              "Common error codes"
            ]
          },
          {
            "type": "ordered-list",
            "items": [
              "400 — Bad request — Invalid parameters or malformed request",
              "401 — Unauthorized — Invalid or missing API key",
              "402 — Payment required — Insufficient credits",
              "403 — Forbidden — Access denied to this resource",
              "404 — Not found — Invalid model, endpoint, or resource",
              "429 — Rate limited — Too many requests, check rate limit headers",
              "500 — Server error — Internal error, try again later",
              "502 — Bad upstream response — e.g. invalid_tool_calls (auto-refunded)",
              "503 — Service unavailable — Model temporarily unavailable"
            ]
          },
          {
            "type": "ordered-list",
            "items": [
              "missing_api_key — Authorization header not provided.",
              "invalid_api_key — API key is invalid or revoked.",
              "credits_exhausted — No credits remaining on the account.",
              "plus_required — Model requires a Gab AI Plus subscription.",
              "rate_limit_exceeded — Daily request limit reached.",
              "invalid_model — Model not found or disabled.",
              "unsupported_tool_calling — tools was passed to a model with function_calling: false. Check /v1/models capabilities.",
              "unsupported_image_input — image_url / input_image part was passed to a model with image_input: false.",
              "invalid_tool_calls — Every tool_call returned by the model was malformed (invalid JSON args, unknown tool name, etc.). The inference is automatically refunded; offending calls are included in the error payload.",
              "file_not_found — A file_id reference could not be resolved or is not owned by the API key user.",
              "inference_failed — Upstream model provider failed to generate a response."
            ]
          },
          {
            "type": "code",
            "code": "{\n  \"error\": {\n    \"message\": \"Insufficient credits to complete request\",\n    \"type\": \"insufficient_credits\",\n    \"code\": \"credits_exhausted\",\n    \"param\": null\n  }\n}",
            "language": "json",
            "title": "Error Response Format"
          },
          {
            "type": "section",
            "title": "Rate Limit Headers",
            "copy": "All API responses include rate limit headers to help you manage your usage:"
          },
          {
            "type": "ordered-list",
            "items": [
              "X-RateLimit-Limit — Maximum requests allowed per day (10,000 for Plus)",
              "X-RateLimit-Remaining — Number of requests remaining in the current period",
              "X-RateLimit-Reset — Unix timestamp when the rate limit resets"
            ]
          },
          {
            "type": "tip",
            "tipType": "tip",
            "title": "Handling Rate Limits",
            "copy": "When you receive a 429 error, check the X-RateLimit-Reset header to know when you can resume making requests."
          }
        ],
        "ogImage": "guides/social-cards/63805245-1520-446c-bea5-050e5cb650dc.png"
      },
      "guideSeo": {
        "readMinutes": 12,
        "difficulty": "Intermediate",
        "articleSection": "API",
        "audience": "Developers and API integrators",
        "about": [
          "REST API",
          "Chat completions",
          "Image generation",
          "Video generation",
          "Embeddings",
          "Text-to-speech"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Authentication",
          "href": "/docs/api-auth"
        },
        "next": {
          "label": "Claude Code",
          "href": "/docs/claude-code"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/api-endpoints"
    },
    {
      "guideType": "docs",
      "slug": "privacy",
      "title": "Privacy & Values",
      "description": "Understand what context Gab AI sends to models and services, and how to manage saved data and access.",
      "badge": "Reference",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "reference",
      "navOrder": 31,
      "navIcon": "shield-halved",
      "searchKeywords": [
        "privacy",
        "values",
        "christian",
        "security",
        "data"
      ],
      "meta": {
        "title": "Privacy, Data & Access Controls | Gab AI Docs",
        "description": "Understand what context Gab AI sends to models and services, and how to manage saved data and access.",
        "keywords": "Gab AI privacy, model context, memory controls, data export, connected accounts",
        "pageCopy": [
          {
            "type": "section",
            "title": "Data used to answer your request",
            "copy": "Gab AI processes your message through its model and service integrations. Depending on the task, the request can include conversation context, attached file contents, saved memories, project files, or information read from a connected website or account. Relevant content is sent to the selected provider to produce the answer. Personal information you include in that content is not guaranteed to be removed or anonymized."
          },
          {
            "type": "section",
            "title": "Choose memory and conversation settings",
            "copy": "Settings → Memory lets you inspect, add, edit, export, and delete saved memories. Use memories in conversations and Learn from conversations are separate controls. Turning learning off does not delete existing memories or disable their use. Temporary chats stay out of ordinary history and never create new memories; they can read existing personalization only when you opt in. Temporary mode does not prevent the model from processing the prompt you send."
          },
          {
            "type": "section",
            "title": "Control files and connected services",
            "copy": "Choose which files to attach and which Mac folders, websites, apps, or connected accounts Work may access. Files Work reads can be processed by Gab AI’s cloud agent and model providers. Plugin installation is separate from authorization. Disconnecting a service prevents subsequent access but cannot undo an action already accepted by that service."
          },
          {
            "type": "section",
            "title": "Export and delete data",
            "copy": "Use Settings → General → Download your data for the supported account archive, or GET /v1/account/export through the developer API. Deleted and temporary chats are excluded from that export. Use conversation controls to delete chats, Settings → Memory for saved facts, and Settings → Account for account deletion. Review the Privacy Policy for retention, processing, and deletion terms."
          },
          {
            "type": "section",
            "title": "Our Values",
            "copy": "Gab AI is built on a foundation of American values and principles:"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "",
            "items": [
              "Free Speech — We believe in the First Amendment and open discourse",
              "Privacy — Your data belongs to you, not us or advertisers",
              "Independence — We operate outside Big Tech control and influence",
              "Christian Values — Our Arya AI model reflects traditional values",
              "Transparency — We're honest about how our platform works",
              "User Empowerment — Technology should serve you, not control you"
            ]
          },
          {
            "type": "section",
            "title": "Arya AI: Values-Aligned AI",
            "copy": "Arya is our exclusive AI model built with Christian values and conservative principles. Unlike mainstream AI models that may exhibit political bias, Arya provides helpful responses aligned with traditional values.\n\nArya is named to evoke nobility and honor. She's helpful, knowledgeable, and respectful—providing assistance without the political lectures you might encounter with other AI systems."
          },
          {
            "type": "section",
            "title": "Independent from Big Tech",
            "copy": "Gab AI is built by Gab, a company that has been fighting for free speech and user privacy since 2016. We're not owned by Big Tech, we don't take their money, and we don't answer to their censorship demands.\n\nThis independence allows us to build AI tools that truly serve users, not corporate interests or political agendas."
          },
          {
            "type": "section",
            "title": "Legal Documents",
            "copy": "For complete details, review our legal documents:"
          },
          {
            "type": "quick-links",
            "items": [
              {
                "title": "Privacy Policy",
                "label": "Privacy Policy",
                "description": "",
                "href": "/privacy-policy"
              },
              {
                "title": "Terms of Service",
                "label": "Terms of Service",
                "description": "",
                "href": "/terms-of-service"
              },
              {
                "title": "Terms of Sale",
                "label": "Terms of Sale",
                "description": "",
                "href": "/terms-of-sale"
              },
              {
                "title": "CCPA Notice",
                "label": "CCPA Notice",
                "description": "",
                "href": "/ccpa"
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/7b599ab3-6b8b-43e3-83cc-d117eb16fbc6.png"
      },
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Beginner",
        "articleSection": "Privacy",
        "about": [
          "Model context",
          "Memory controls",
          "Data export",
          "Connected service access"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Download Your Data",
          "href": "/docs/data-export"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/privacy"
    },
    {
      "guideType": "docs",
      "slug": "credits",
      "title": "Credits System",
      "description": "Understand personal credits, included allowances, usage, and planned Business credit pools.",
      "badge": "Core Concepts",
      "badgeColor": "#3B82F6",
      "navSectionSlug": "core-concepts",
      "navOrder": 4,
      "navIcon": "coins",
      "searchKeywords": [
        "credits",
        "pricing",
        "cost",
        "tokens",
        "usage",
        "billing"
      ],
      "meta": {
        "title": "Gab AI Credits Explained: Pricing, Usage & Top-Ups",
        "description": "Understand personal credits, included allowances, usage, and planned Business credit pools.",
        "keywords": "Gab AI credits, AI credits, AI pricing, credit cost, AI usage billing, top up credits, image generation cost, video generation cost, AI subscription pricing",
        "pageCopy": [
          {
            "type": "section",
            "title": "How credits work",
            "copy": "Credits pay for eligible AI models, Work, media generation, and other paid features. The model, input, output, and options determine the cost. Review the current model card or estimate before starting; static examples are not a quote for your next request."
          },
          {
            "type": "section",
            "title": "Personal monthly credits",
            "copy": "Plus includes 2,000 monthly credits and Plus Premium includes 12,000. Included credits expire at the end of their allowance period. Purchased personal credits do not expire monthly. Your subscription and credit settings show your balance and allowance details."
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Default model chat",
            "copy": "Plus includes default-model chat in the app at no credit cost. API requests use credits. Other models and tools can use credits even when you have Plus."
          },
          {
            "type": "section",
            "title": "Usage and pending amounts",
            "copy": "A displayed estimate or temporary reservation is not always the final cost. Usage can depend on the amount of context processed, generated output, and tool calls. Work uses credits as it runs, and media within a task can add costs. Developer text requests with reliable usage settle in fractional credits."
          },
          {
            "type": "section",
            "title": "Managing personal credits",
            "copy": "Review your balance and recent usage in Settings → Credits. Credit packs and payment options depend on your platform. The current purchase screen shows the pack, price, and credit amount before you confirm. Eligible web accounts can configure automatic top-up with the limits shown in their settings."
          },
          {
            "type": "section",
            "title": "Gab AI Business",
            "copy": "Business uses a separate company credit pool funded by paid Plus and Plus Premium seats. Company and member limits include completed and pending usage. Personal subscriptions and purchased credits stay in Personal. Business setup, upgrades, credit purchases, and billing are on the website, including for existing Apple and Google subscribers."
          },
          {
            "type": "quick-links",
            "title": "More about credits",
            "items": [
              {
                "title": "Pricing",
                "description": "Current individual plans and credit purchase options.",
                "href": "/docs/pricing-guide"
              },
              {
                "title": "Business credits and limits",
                "description": "Company pools, member limits, and pending usage.",
                "href": "/docs/business-credits"
              },
              {
                "title": "Business for existing accounts",
                "description": "Personal subscription and credit treatment during setup.",
                "href": "/docs/business-upgrade"
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/c291d068-c9e2-40ab-b2ff-4d6e39c46c4a.png"
      },
      "guideSeo": {
        "readMinutes": 6,
        "difficulty": "Beginner",
        "articleSection": "Pricing",
        "about": [
          "AI credits",
          "AI pricing",
          "Usage billing"
        ]
      },
      "navigation": {
        "previous": {
          "label": "AI Models",
          "href": "/docs/models"
        },
        "next": {
          "label": "Pricing",
          "href": "/docs/pricing-guide"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/credits",
      "updatedAt": "2026-09-28T12:00:00.000Z"
    },
    {
      "guideType": "docs",
      "slug": "opencode",
      "title": "OpenCode",
      "description": "Configure Gab AI in OpenCode v1 or v2 with the OpenAI-compatible Responses API.",
      "badge": "Coding CLI",
      "badgeColor": "#8B5CF6",
      "navSectionSlug": "developer",
      "navOrder": 12,
      "navIcon": "plug",
      "searchKeywords": [
        "opencode",
        "open code",
        "openai compatible",
        "cli",
        "coding",
        "tool calling",
        "v1",
        "v2",
        "responses",
        "provider package"
      ],
      "meta": {
        "title": "Use OpenCode with Gab AI: OpenAI-Compatible Agent Setup",
        "description": "Configure Gab AI in OpenCode v1 or v2 with the OpenAI-compatible Responses API.",
        "keywords": "OpenCode setup, Gab AI OpenCode, OpenAI-compatible coding agent, AI coding CLI, tool calling, agent loop, AI pair programming, function calling, autonomous coding agent",
        "pageCopy": [
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "Prerequisites",
            "items": [
              "A Gab AI Plus subscription",
              "A Gab AI API key (gab.ai → Settings → API)",
              "OpenCode installed locally. Run opencode --version and use the configuration format for your version."
            ]
          },
          {
            "type": "section",
            "title": "Choose the Provider Package",
            "copy": "Declare Gab AI as a custom provider in opencode.json or opencode.jsonc in your project, or in ~/.config/opencode/opencode.json for global use. Set the base URL to https://gab.ai/v1. The provider package selects the API protocol; choose the Responses adapter for the setup below."
          },
          {
            "type": "unordered-list",
            "items": [
              "OpenCode v1: use npm: \"@ai-sdk/openai\" under provider.gab-ai, with options.baseURL.",
              "OpenCode v2: use package: \"@opencode/ai/providers/openai-compatible/responses\" under providers.gab-ai, with settings.baseURL. Keep the /responses suffix on the package name."
            ]
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Why the Package Names Differ",
            "copy": "@ai-sdk/openai selects /v1/responses in OpenCode v1. OpenCode v2 has its own native Responses adapter, @opencode/ai/providers/openai-compatible/responses. The plain @ai-sdk/openai-compatible and @opencode/ai/providers/openai-compatible adapters select /v1/chat/completions, which Gab AI also supports. They use a different protocol from the Responses examples in this guide. The package choice does not guarantee a fixed credit charge per tool turn; text requests with reliable usage settle in fractional credits based on usage."
          },
          {
            "type": "section",
            "title": "OpenCode v1 Configuration",
            "copy": "GET https://gab.ai/v1/agent-setup?client=opencode currently generates a v1 configuration with @ai-sdk/openai, live model limits, modalities and tool_call flags. Save the generated config to a separate file, then merge its Gab AI provider entry into your existing config. For a new config, you can use the generated file directly. Use auto only for the main agent model; use a concrete model slug for auxiliary calls."
          },
          {
            "type": "code",
            "language": "bash",
            "title": "Fetch the v1 Configuration",
            "code": "curl -fsSL \"https://gab.ai/v1/agent-setup?client=opencode\" | jq .config > gab-opencode-v1.json"
          },
          {
            "type": "code",
            "language": "json",
            "title": "OpenCode v1: opencode.json",
            "code": "{\n  \"$schema\": \"https://opencode.ai/config.json\",\n  \"model\": \"gab-ai/claude-sonnet-4-5\",\n  \"provider\": {\n    \"gab-ai\": {\n      \"npm\": \"@ai-sdk/openai\",\n      \"name\": \"Gab AI\",\n      \"options\": {\n        \"baseURL\": \"https://gab.ai/v1\"\n      },\n      \"models\": {\n        \"claude-sonnet-4-5\": {\n          \"name\": \"Claude Sonnet 4.5\",\n          \"limit\": {\n            \"context\": 200000,\n            \"output\": 64000\n          },\n          \"modalities\": {\n            \"input\": [\n              \"text\",\n              \"image\"\n            ],\n            \"output\": [\n              \"text\"\n            ]\n          },\n          \"tool_call\": true\n        }\n      }\n    }\n  }\n}"
          },
          {
            "type": "ordered-list",
            "title": "Connect Your API Key in v1",
            "items": [
              "Save or merge the v1 configuration, then start OpenCode in your project.",
              "Run /connect, select Gab AI and paste your Gab AI API key.",
              "Run /models and select a Gab AI model."
            ]
          },
          {
            "type": "section",
            "title": "OpenCode v2 Configuration",
            "copy": "Use the plural providers map, package and settings fields shown below. This example uses GAB_API_KEY for the credential; set it in the environment of the OpenCode server, or connect Gab AI through /connect. Save or merge the example into your config, then use /models to select a Gab AI model. The model limits below are an example from the live catalog; recheck /v1/models when adding or changing models."
          },
          {
            "type": "code",
            "language": "json",
            "title": "OpenCode v2: opencode.json",
            "code": "{\n  \"$schema\": \"https://opencode.ai/config.json\",\n  \"model\": \"gab-ai/claude-sonnet-4-5\",\n  \"providers\": {\n    \"gab-ai\": {\n      \"name\": \"Gab AI\",\n      \"env\": [\n        \"GAB_API_KEY\"\n      ],\n      \"package\": \"@opencode/ai/providers/openai-compatible/responses\",\n      \"settings\": {\n        \"baseURL\": \"https://gab.ai/v1\"\n      },\n      \"models\": {\n        \"claude-sonnet-4-5\": {\n          \"name\": \"Claude Sonnet 4.5\",\n          \"limit\": {\n            \"context\": 200000,\n            \"output\": 64000\n          },\n          \"capabilities\": {\n            \"tools\": true,\n            \"input\": [\n              \"text\",\n              \"image\"\n            ],\n            \"output\": [\n              \"text\"\n            ]\n          }\n        }\n      }\n    }\n  }\n}"
          },
          {
            "type": "tip",
            "tipType": "warning",
            "title": "The Generated Config Is Currently v1",
            "copy": "Do not paste the generated /v1/agent-setup config unchanged into a native v2 setup. Rename provider to providers, replace npm with package: \"@opencode/ai/providers/openai-compatible/responses\", and move options to settings. Keep each model's name and limit. Replace tool_call and modalities with capabilities.tools, capabilities.input and capabilities.output, as shown in the v2 example."
          },
          {
            "type": "tip",
            "tipType": "info",
            "title": "Model IDs and Aliases",
            "copy": "Use a model ID from /v1/models or an alias listed by that endpoint. The provider prefix is gab-ai, so the selectable model reference in the examples is gab-ai/claude-sonnet-4-5. Display names are labels, not API model IDs."
          },
          {
            "type": "section",
            "title": "Discover Compatible Models",
            "copy": "Tool/function calling is required for OpenCode's agent loop. GET /v1/models is public and includes context_window, max_output_tokens, capabilities.function_calling and recommended_for. Match each model's OpenCode limits and tool/media capabilities to those values. In v1 use tool_call and modalities; in v2 use capabilities.tools, capabilities.input and capabilities.output."
          },
          {
            "type": "code",
            "language": "bash",
            "title": "List Coding Models with Tool Support",
            "code": "curl -fsSL https://gab.ai/v1/models | jq -r ' .data[] | select(.capabilities.function_calling == true) | select(any(.recommended_for[]?; . == \"coding\")) | .id'"
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "Supported Features",
            "items": [
              "OpenAI Responses API (/v1/responses) with the recommended adapters above",
              "OpenAI Chat Completions (/v1/chat/completions) with the plain compatible adapters",
              "Tool/function calling and streaming for models that advertise those capabilities",
              "Vision input for models that advertise image_input",
              "Response continuation with previous_response_id when the client uses it; choosing a Responses adapter alone does not guarantee the client omits earlier messages"
            ]
          },
          {
            "type": "unordered-list",
            "variant": "check",
            "title": "Troubleshooting",
            "items": [
              "401 authentication_error: re-connect your Gab AI key, or check that GAB_API_KEY is available to the v2 OpenCode server.",
              "400 invalid_model: check the model ID or alias against /v1/models. In v2, modelID can map a custom OpenCode model key to the ID sent to Gab AI.",
              "Provider settings ignored or models missing: check your OpenCode version. v1 uses provider/npm/options; v2 uses providers/package/settings.",
              "Requests reach /v1/chat/completions: use @ai-sdk/openai in v1, or @opencode/ai/providers/openai-compatible/responses in v2, to select Responses.",
              "Tool calls misbehaving: confirm the Gab model advertises capabilities.function_calling and that your OpenCode configuration enables tool support.",
              "Use https://gab.ai/v1 as baseURL. The adapter appends /responses or /chat/completions; do not append either endpoint yourself."
            ]
          },
          {
            "type": "quick-links",
            "title": "OpenCode Reference",
            "items": [
              {
                "label": "OpenCode v1 provider configuration",
                "href": "https://opencode.ai/docs/providers/"
              },
              {
                "label": "OpenCode v2 provider packages",
                "href": "https://opencode.ai/v2/docs/providers"
              },
              {
                "label": "OpenCode v2 model capabilities and limits",
                "href": "https://opencode.ai/v2/docs/models"
              }
            ]
          }
        ],
        "ogImage": "guides/social-cards/ba4f30cd-16d0-4073-83ea-c91be46935b3.png"
      },
      "guideSeo": {
        "readMinutes": 5,
        "difficulty": "Intermediate",
        "articleSection": "API Integrations",
        "audience": "Developers",
        "about": [
          "OpenCode",
          "AI coding agents",
          "Tool calling"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Pi CLI",
          "href": "/docs/pi-cli"
        },
        "next": {
          "label": "Using Gab AI API with Cursor IDE",
          "href": "/docs/cursor-ide"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/opencode",
      "updatedAt": "2026-10-02T16:00:00.000Z"
    }
  ]
}
